export interface TourData {
  title: string;
  location: string;
  duration: string;
  difficulty: string;
  heroImage: string;
  heroImageAlt: string;
  description: string;
  highlights: string[];
  bestTime: string;
  groupSize: string;
}

export interface ItineraryDay {
  day: number;
  title: string;
  description: string;
  activities: string[];
  meals: string;
  accommodation: string;
  locations?: {name: string;lat: number;lon: number;}[];
}

export interface PricingTier {
  name: string;
  vehicleType: string;
  capacity: string;
  pricePerPerson: number;
  features: string[];
}

export interface GalleryImage {
  url: string;
  alt: string;
  caption: string;
}

export interface Testimonial {
  name: string;
  location: string;
  rating: number;
  date: string;
  comment: string;
  avatar: string;
  avatarAlt: string;
  tourType: string;
}

export interface Preparation {
  category: string;
  icon: string;
  items: string[];
}

export interface TourDetails {
  id: string;
  tourData: TourData;
  itinerary: ItineraryDay[];
  inclusions: string[];
  exclusions: string[];
  pricingTiers: PricingTier[];
  galleryImages: GalleryImage[];
  testimonials: Testimonial[];
  preparations: Preparation[];
  permitRequired?: boolean;
  medicalRequired?: boolean;
}

// Tour 1: Char Dham Yatra
const charDhamYatra: TourDetails = {
  id: '1',
  tourData: {
    title: 'Char Dham Yatra - Complete Sacred Circuit',
    location: 'Uttarakhand',
    duration: '10 Days / 9 Nights',
    difficulty: 'Moderate',
    heroImage: "https://images.unsplash.com/photo-1545427692-1cdb59c34c62",
    heroImageAlt: 'Majestic snow-capped Himalayan mountain peaks with ancient temple in foreground during golden hour sunrise',
    description: 'Embark on a transformative spiritual journey through the sacred Char Dham circuit of Uttarakhand. This comprehensive pilgrimage covers Yamunotri, Gangotri, Kedarnath, and Badrinath - the four most revered Hindu shrines nestled in the majestic Himalayas.',
    highlights: [
    'Visit all four sacred dhams: Yamunotri, Gangotri, Kedarnath, and Badrinath',
    'Witness the divine Ganga Aarti at Haridwar and Rishikesh',
    'Trek to Kedarnath temple with pony/doli options available',
    'Experience the spiritual energy of ancient Himalayan temples',
    'Comfortable accommodation at each destination',
    'Expert local guides with deep spiritual knowledge',
    'All necessary permits and registrations handled'],

    bestTime: 'May to October',
    groupSize: '4-12 people'
  },
  itinerary: [
  {
    day: 1,
    title: 'Haridwar to Barkot',
    description: 'Begin your sacred journey with pickup from Haridwar. Drive through scenic mountain roads to Barkot.',
    activities: [
    'Pickup from Haridwar railway station',
    'Visit Kempty Falls',
    'Drive through Mussoorie',
    'Evening check-in at Barkot hotel'],
    meals: 'Lunch, Dinner',
    accommodation: 'Hotel in Barkot',
    locations: [
    { name: 'Haridwar', lat: 29.9457, lon: 78.1642 },
    { name: 'Barkot', lat: 30.8143, lon: 78.2066 }]

  },
  {
    day: 2,
    title: 'Yamunotri Darshan',
    description: 'Trek to Yamunotri temple, take holy dip in Surya Kund, and return to Barkot.',
    activities: [
    'Drive to Janki Chatti',
    'Trek to Yamunotri temple (6 km)',
    'Holy bath in Surya Kund',
    'Darshan at Yamunotri temple',
    'Return trek to Janki Chatti',
    'Drive back to Barkot'],
    meals: 'Breakfast, Packed Lunch, Dinner',
    accommodation: 'Hotel in Barkot',
    locations: [
    { name: 'Yamunotri', lat: 31.0114, lon: 78.4455 },
    { name: 'Janki Chatti', lat: 31.0003, lon: 78.4397 }]

  },
  {
    day: 3,
    title: 'Barkot to Uttarkashi',
    description: 'Journey to Uttarkashi, the gateway to Gangotri.',
    activities: [
    'Checkout from Barkot',
    'Scenic drive to Uttarkashi',
    'Visit Vishwanath Temple',
    'Evening Ganga Aarti',
    'Check-in at hotel'],
    meals: 'Breakfast, Dinner',
    accommodation: 'Hotel in Uttarkashi',
    locations: [
    { name: 'Uttarkashi', lat: 30.7346, lon: 78.4434 }]

  },
  {
    day: 4,
    title: 'Gangotri Darshan',
    description: 'Visit the source of holy Ganga at Gangotri temple.',
    activities: [
    'Early morning drive to Gangotri',
    'Darshan at Gangotri temple',
    'Visit Pandava Gufa',
    'Explore Gaumukh viewpoint',
    'Return to Uttarkashi'],
    meals: 'Breakfast, Packed Lunch, Dinner',
    accommodation: 'Hotel in Uttarkashi',
    locations: [
    { name: 'Gangotri', lat: 30.9993, lon: 78.9392 }]

  },
  {
    day: 5,
    title: 'Uttarkashi to Guptkashi',
    description: 'Journey towards Kedarnath base, stopping at scenic locations.',
    activities: [
    'Drive to Guptkashi via Tehri Dam',
    'Photo stops at viewpoints',
    'Visit Ardh Narishwar Temple',
    'Evening free time',
    'Hotel check-in'],
    meals: 'Breakfast, Dinner',
    accommodation: 'Hotel in Guptkashi',
    locations: [
    { name: 'Guptkashi', lat: 30.5252, lon: 79.0820 }]

  },
  {
    day: 6,
    title: 'Kedarnath Yatra',
    description: 'Trek or take helicopter to Kedarnath, one of the twelve Jyotirlingas.',
    activities: [
    'Early morning drive to Gaurikund',
    'Begin trek to Kedarnath (16 km) or take helicopter',
    'Check-in at Kedarnath accommodation',
    'Evening darshan at Kedarnath temple',
    'Attend evening aarti'],
    meals: 'Breakfast, Packed Lunch, Dinner',
    accommodation: 'Guest house in Kedarnath',
    locations: [
    { name: 'Kedarnath', lat: 30.7346, lon: 79.0669 },
    { name: 'Gaurikund', lat: 30.5301, lon: 79.0605 }]

  },
  {
    day: 7,
    title: 'Kedarnath to Guptkashi',
    description: 'Morning darshan and return journey to Guptkashi.',
    activities: [
    'Early morning darshan',
    'Trek down to Gaurikund',
    'Drive back to Guptkashi',
    'Rest and relaxation',
    'Overnight stay'],
    meals: 'Breakfast, Packed Lunch, Dinner',
    accommodation: 'Hotel in Guptkashi',
    locations: [
    { name: 'Guptkashi', lat: 30.5252, lon: 79.0820 }]

  },
  {
    day: 8,
    title: 'Guptkashi to Badrinath',
    description: 'Journey to the sacred Badrinath temple.',
    activities: [
    'Drive to Badrinath via Joshimath',
    'Visit Narsingh Temple',
    'Check-in at Badrinath hotel',
    'Evening darshan at Badrinath temple',
    'Attend Badri Vishal Aarti'],
    meals: 'Breakfast, Dinner',
    accommodation: 'Hotel in Badrinath',
    locations: [
    { name: 'Badrinath', lat: 30.7433, lon: 79.4938 },
    { name: 'Joshimath', lat: 30.5556, lon: 79.5641 }]

  },
  {
    day: 9,
    title: 'Badrinath Local Sightseeing',
    description: 'Explore Badrinath and nearby attractions.',
    activities: [
    'Morning darshan at Badrinath',
    'Visit Mana Village (last village of India)',
    'Explore Vyas Gufa and Ganesh Gufa',
    'Visit Bheem Pul',
    'See Saraswati River origin',
    'Return to hotel'],
    meals: 'Breakfast, Packed Lunch, Dinner',
    accommodation: 'Hotel in Badrinath',
    locations: [
    { name: 'Badrinath', lat: 30.7433, lon: 79.4938 },
    { name: 'Mana Village', lat: 30.7577, lon: 79.4700 }]

  },
  {
    day: 10,
    title: 'Badrinath to Haridwar',
    description: 'Complete your sacred journey returning to Haridwar.',
    activities: [
    'Early checkout from Badrinath',
    'Long drive back to Haridwar',
    'En-route photo stops',
    'Arrive at Haridwar',
    'Drop at railway station'],
    meals: 'Breakfast, Packed Lunch',
    accommodation: 'Journey ends',
    locations: [
    { name: 'Haridwar', lat: 29.9457, lon: 78.1642 }]

  }],

  inclusions: [
  'AC vehicle for entire journey',
  'Accommodation on sharing basis',
  'Daily breakfast and dinner',
  'All permits and registrations',
  'Local guide services',
  'All toll taxes and parking fees',
  'Driver allowances',
  'First aid kit'],

  exclusions: [
  'Lunch during travel',
  'Pony/Doli charges for treks',
  'Helicopter charges (if opted)',
  'Personal expenses',
  'Tips to guides and drivers',
  'Travel insurance',
  'Any items not mentioned in inclusions'],

  pricingTiers: [
  {
    name: 'Economy Package',
    vehicleType: 'Maruti Ertiga',
    capacity: 'Up to 6 people',
    pricePerPerson: 18500,
    features: [
    'Standard 3-star hotels',
    'AC vehicle throughout',
    'Basic meals (breakfast & dinner)',
    'Experienced driver',
    'All permits included']

  },
  {
    name: 'Deluxe Package',
    vehicleType: 'Toyota Innova Crysta',
    capacity: 'Up to 6 people',
    pricePerPerson: 24500,
    features: [
    'Deluxe 4-star hotels',
    'Premium AC vehicle',
    'Enhanced meals with variety',
    'Professional tour guide',
    'All permits and insurance',
    'Welcome kit']

  },
  {
    name: 'Premium Package',
    vehicleType: 'Toyota Fortuner',
    capacity: 'Up to 6 people',
    pricePerPerson: 32000,
    features: [
    'Luxury 5-star hotels',
    'Premium SUV with extra comfort',
    'All meals (breakfast, lunch, dinner)',
    'Expert spiritual guide',
    'VIP darshan arrangements',
    'Helicopter option for Kedarnath',
    'Travel insurance included',
    'Premium welcome hamper']

  }],

  galleryImages: [
  {
    url: "https://images.unsplash.com/photo-1654355380835-0e5c9dace62a",
    alt: 'Badrinath Temple exterior with colorful facade and devotees',
    caption: 'Badrinath Temple at Dawn'
  },
  {
    url: "https://images.unsplash.com/photo-1730446224699-8d528d177c71",
    alt: 'Kedarnath temple surrounded by snow-covered peaks',
    caption: 'Sacred Kedarnath Dham'
  },
  {
    url: "https://img.rocket.new/generatedImages/rocket_gen_img_187eec74f-1764135733571.png",
    alt: 'Gangotri temple on the banks of Bhagirathi river',
    caption: 'Gangotri - Source of Ganga'
  },
  {
    url: "https://img.rocket.new/generatedImages/rocket_gen_img_118978cad-1764135736656.png",
    alt: 'Yamunotri temple with thermal springs',
    caption: 'Divine Yamunotri Dham'
  },
  {
    url: "https://images.unsplash.com/photo-1454414022654-e4b62ef99078",
    alt: 'Mountain valley with winding river and prayer flags',
    caption: 'Scenic Himalayan Valleys'
  },
  {
    url: "https://images.unsplash.com/photo-1649518755916-1471b66d654c",
    alt: 'Evening Ganga Aarti ceremony with oil lamps',
    caption: 'Evening Aarti at Temple'
  }],

  testimonials: [
  {
    name: 'Rajesh Kumar',
    location: 'Delhi',
    rating: 5,
    date: '15 Oct 2024',
    comment: 'An absolutely divine experience! The arrangements were perfect, and our driver was very experienced in mountain roads. The spiritual energy at each dham is overwhelming. Highly recommend Rawat Travels for Char Dham Yatra.',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1f6ba27b0-1763296311975.png",
    avatarAlt: 'Rajesh Kumar profile photo',
    tourType: 'Char Dham Yatra'
  },
  {
    name: 'Priya Sharma',
    location: 'Mumbai',
    rating: 5,
    date: '8 Sep 2024',
    comment: 'Best decision to book with Rawat Travels. The hotels were clean, food was good, and the entire journey was well-planned. The guide\'s knowledge about the temples added so much value to our pilgrimage.',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_10458bf2c-1763293523905.png",
    avatarAlt: 'Priya Sharma profile photo',
    tourType: 'Char Dham Yatra'
  },
  {
    name: 'Suresh Patel Family',
    location: 'Ahmedabad',
    rating: 5,
    date: '22 Aug 2024',
    comment: 'Traveled with my elderly parents. The team was very supportive and made arrangements keeping their comfort in mind. The pony arrangements for Kedarnath were smooth. Thank you for making this journey memorable!',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1b297a3a8-1763293188713.png",
    avatarAlt: 'Suresh Patel profile photo',
    tourType: 'Char Dham Yatra'
  }],

  preparations: [
  {
    category: 'Essential Documents',
    icon: 'DocumentTextIcon',
    items: [
    'Valid photo ID (Aadhar/Passport/Driving License)',
    'Medical certificate (for senior citizens)',
    'Travel insurance documents',
    'Printout of booking confirmation']

  },
  {
    category: 'Clothing & Gear',
    icon: 'ShoppingBagIcon',
    items: [
    'Warm clothes (jacket, sweaters, thermals)',
    'Comfortable trekking shoes',
    'Raincoat or umbrella',
    'Sun hat and sunglasses',
    'Walking stick for treks',
    'Small backpack for day trips']

  },
  {
    category: 'Health & Medicine',
    icon: 'HeartIcon',
    items: [
    'Personal medications',
    'First aid kit',
    'Altitude sickness pills',
    'Digestive medicines',
    'Pain relief balm',
    'Hand sanitizer and wet wipes']

  },
  {
    category: 'Important Tips',
    icon: 'LightBulbIcon',
    items: [
    'Start light exercise 2 weeks before trip',
    'Avoid alcohol 48 hours before journey',
    'Stay hydrated during the trip',
    'Respect local customs and traditions',
    'Keep emergency contacts handy',
    'Charge all electronic devices before departure']

  }]

};

// Tour 2: Adi Kailash Trek - COMPLETE UPDATE
const adiKailashTrek: TourDetails = {
  id: '2',
  tourData: {
    title: 'Adi Kailash & Om Parvat - Rare Sacred Journey',
    location: 'Uttarakhand',
    duration: '14 Days / 13 Nights',
    difficulty: 'Challenging',
    heroImage: "https://img.rocket.new/generatedImages/rocket_gen_img_13ba6256d-1764144653162.png",
    heroImageAlt: 'Snow-covered mountain peak with natural Om symbol',
    description: 'Embark on the rare pilgrimage to Adi Kailash and witness the divine Om Parvat. This challenging journey takes you through remote Himalayan terrain, ancient trade routes, and pristine mountain landscapes.',
    highlights: [
    'Witness the natural Om formation on Om Parvat',
    'Trek to Adi Kailash base camp at 19,500 ft',
    'Visit ancient Kailash Mansarovar route',
    'Experience untouched Himalayan villages',
    'Cross high altitude passes',
    'Explore Indo-Tibet border region'],

    bestTime: 'June to September',
    groupSize: '6-10 people'
  },
  itinerary: [
  {
    day: 1,
    title: 'Delhi to Kathgodam',
    description: 'Board overnight train from Delhi to Kathgodam.',
    activities: [
    'Report at Delhi railway station',
    'Board Ranikhet Express',
    'Overnight journey through plains',
    'Reach foothills of Himalayas'],

    meals: 'Dinner on train',
    accommodation: 'AC 2-tier train berth'
  },
  {
    day: 2,
    title: 'Kathgodam to Dharchula',
    description: 'Scenic drive through Kumaon hills to border town Dharchula.',
    activities: [
    'Early morning arrival at Kathgodam',
    'Drive through Almora and Pithoragarh',
    'Lunch at scenic location',
    'Reach Dharchula by evening',
    'Hotel check-in and briefing'],

    meals: 'Breakfast, Lunch, Dinner',
    accommodation: 'Hotel in Dharchula'
  },
  {
    day: 3,
    title: 'Dharchula to Gunji',
    description: 'Drive along Indo-Nepal border to remote village of Gunji.',
    activities: [
    'Early breakfast',
    'Drive along Kali River',
    'Pass through checkpoints',
    'Stop at Narayan Ashram',
    'Reach Gunji village',
    'Acclimatization walk'],

    meals: 'Breakfast, Packed Lunch, Dinner',
    accommodation: 'Guest house in Gunji'
  },
  {
    day: 4,
    title: 'Gunji to Kalapani',
    description: 'Trek to historic Kalapani, last inhabited place before Lipulekh Pass.',
    activities: [
    'Morning acclimatization',
    'Begin trek to Kalapani',
    'Cross several streams',
    'Reach Kalapani base camp',
    'Set up camp',
    'Evening briefing'],

    meals: 'Breakfast, Lunch, Dinner',
    accommodation: 'Camping at Kalapani'
  },
  {
    day: 5,
    title: 'Kalapani Acclimatization',
    description: 'Rest day for altitude acclimatization at 12,000 ft.',
    activities: [
    'Light morning walk',
    'Visit Kali temple',
    'Explore surrounding area',
    'Yoga and breathing exercises',
    'Equipment check',
    'Rest and hydration'],

    meals: 'Breakfast, Lunch, Dinner',
    accommodation: 'Camping at Kalapani'
  },
  {
    day: 6,
    title: 'Kalapani to Jollingkong',
    description: 'Trek through challenging terrain to Jollingkong base.',
    activities: [
    'Early morning start',
    'Gradual ascent through moraine',
    'Cross glacial streams',
    'Lunch break at scenic spot',
    'Reach Jollingkong camp',
    'Evening rest'],

    meals: 'Breakfast, Packed Lunch, Dinner',
    accommodation: 'Camping at Jollingkong'
  },
  {
    day: 7,
    title: 'Jollingkong - Om Parvat View',
    description: 'Witness the divine Om formation on Om Parvat mountain.',
    activities: [
    'Pre-dawn wake up',
    'Trek to Om Parvat viewpoint',
    'Witness sunrise on Om Parvat',
    'Photography and prayers',
    'Return to camp',
    'Rest for the day'],

    meals: 'Breakfast, Lunch, Dinner',
    accommodation: 'Camping at Jollingkong'
  },
  {
    day: 8,
    title: 'Jollingkong to Nabhi Dhang',
    description: 'Trek to high altitude camp of Nabhi Dhang.',
    activities: [
    'Morning trek start',
    'Cross high passes',
    'Navigate rocky terrain',
    'Reach Nabhi Dhang',
    'Set up camp at 14,500 ft',
    'Acclimatization walk'],

    meals: 'Breakfast, Packed Lunch, Dinner',
    accommodation: 'Camping at Nabhi Dhang'
  },
  {
    day: 9,
    title: 'Adi Kailash Parikrama',
    description: 'Complete the sacred circumambulation of Adi Kailash peak.',
    activities: [
    'Very early morning start',
    'Begin Adi Kailash parikrama',
    'Trek around the sacred peak',
    'Witness breathtaking views',
    'Complete parikrama circuit',
    'Return to camp'],

    meals: 'Breakfast, Packed Lunch, Dinner',
    accommodation: 'Camping at Nabhi Dhang'
  },
  {
    day: 10,
    title: 'Nabhi Dhang to Gunji',
    description: 'Descend back to Gunji village.',
    activities: [
    'Morning descent',
    'Retrace path through camps',
    'Stop at Kalapani',
    'Continue to Gunji',
    'Celebrate successful trek',
    'Hot shower and rest'],

    meals: 'Breakfast, Lunch, Dinner',
    accommodation: 'Guest house in Gunji'
  },
  {
    day: 11,
    title: 'Gunji to Dharchula',
    description: 'Drive back to Dharchula border town.',
    activities: [
    'Morning checkout',
    'Scenic drive to Dharchula',
    'Stop at viewpoints',
    'Shopping for local handicrafts',
    'Hotel check-in',
    'Farewell dinner'],

    meals: 'Breakfast, Lunch, Dinner',
    accommodation: 'Hotel in Dharchula'
  },
  {
    day: 12,
    title: 'Dharchula to Kathgodam',
    description: 'Return journey to Kathgodam.',
    activities: [
    'Checkout after breakfast',
    'Long drive to Kathgodam',
    'En-route sightseeing',
    'Reach Kathgodam evening',
    'Hotel check-in',
    'Rest'],

    meals: 'Breakfast, Lunch, Dinner',
    accommodation: 'Hotel in Kathgodam'
  },
  {
    day: 13,
    title: 'Kathgodam to Delhi',
    description: 'Board return train to Delhi.',
    activities: [
    'Morning at leisure',
    'Local market visit',
    'Evening train to Delhi',
    'Overnight journey',
    'Trip memories'],

    meals: 'Breakfast, Dinner on train',
    accommodation: 'AC 2-tier train berth'
  },
  {
    day: 14,
    title: 'Arrive Delhi',
    description: 'Complete your Adi Kailash expedition.',
    activities: [
    'Morning arrival at Delhi',
    'End of journey',
    'Take home memories'],

    meals: 'Breakfast on train',
    accommodation: 'Journey ends'
  }],

  inclusions: [
  'All transportation (train & SUV)',
  'Camping equipment and tents',
  'All meals during trek',
  'Experienced trek leader',
  'Support staff and porters',
  'Inner line permits',
  'Medical kit and oxygen cylinders',
  'Accommodation as per itinerary'],

  exclusions: [
  'Personal trekking gear',
  'Travel insurance (mandatory)',
  'Tips to staff',
  'Personal expenses',
  'Any meals not mentioned',
  'Medical evacuation costs'],

  pricingTiers: [
  {
    name: 'Standard Package',
    vehicleType: 'Mahindra Bolero/Scorpio',
    capacity: 'Up to 8 people',
    pricePerPerson: 125000,
    features: [
    'All meals during trek',
    'Standard camping equipment',
    'Experienced mountain guide',
    'Porter support',
    'Basic medical kit',
    'All permits included']

  },
  {
    name: 'Premium Package',
    vehicleType: 'Toyota Fortuner',
    capacity: 'Up to 6 people',
    pricePerPerson: 145000,
    features: [
    'Enhanced meals with variety',
    'Premium camping equipment',
    'Expert expedition leader',
    'Personal porter for gear',
    'Advanced medical support',
    'Satellite phone access',
    'Photography assistance',
    'Insurance coverage']

  }],

  galleryImages: [
  {
    url: "https://images.unsplash.com/photo-1734794986247-a38beebcb0dc",
    alt: 'Om Parvat mountain showing natural Om symbol in snow',
    caption: 'Divine Om Parvat - Natural Om Formation'
  },
  {
    url: "https://images.unsplash.com/photo-1734794986247-a38beebcb0dc",
    alt: 'Adi Kailash peak with trekkers in foreground',
    caption: 'Sacred Adi Kailash Peak'
  },
  {
    url: "https://images.unsplash.com/photo-1603111847111-f3515bc128bd",
    alt: 'High altitude camping with mountain backdrop',
    caption: 'High Altitude Base Camp'
  },
  {
    url: "https://images.unsplash.com/photo-1724287793610-3ca8be6540c6",
    alt: 'Trekkers crossing mountain stream with peaks behind',
    caption: 'Crossing Himalayan Streams'
  },
  {
    url: "https://images.unsplash.com/photo-1570838978577-fdc5c78f5566",
    alt: 'Sunrise view over snow-capped peaks',
    caption: 'Himalayan Sunrise'
  },
  {
    url: "https://images.unsplash.com/photo-1518804917388-3b0a615c174c",
    alt: 'Prayer flags on mountain pass',
    caption: 'Prayer Flags at High Pass'
  }],

  testimonials: [
  {
    name: 'Amit Singh',
    location: 'Mumbai',
    rating: 5,
    date: '10 Sep 2024',
    comment: 'Life-changing trek! Witnessing Om Parvat was a spiritual experience beyond words. The team handled everything professionally despite challenging conditions. Best expedition of my life!',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_165db465c-1763296624925.png",
    avatarAlt: 'Amit Singh profile photo',
    tourType: 'Adi Kailash Trek'
  },
  {
    name: 'Neha Gupta',
    location: 'Bangalore',
    rating: 5,
    date: '25 Aug 2024',
    comment: 'Extremely well-organized expedition. The guides knew the terrain inside out and ensured our safety at all times. The views of Om Parvat and Adi Kailash are etched in my memory forever.',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_10458bf2c-1763293523905.png",
    avatarAlt: 'Neha Gupta profile photo',
    tourType: 'Adi Kailash Trek'
  },
  {
    name: 'Vikram Malhotra',
    location: 'Chandigarh',
    rating: 5,
    date: '5 Aug 2024',
    comment: 'Challenging but absolutely worth it! The support staff was incredible, and the camping arrangements were better than expected at such remote locations. Highly recommend for serious trekkers.',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1b297a3a8-1763293188713.png",
    avatarAlt: 'Vikram Malhotra profile photo',
    tourType: 'Adi Kailash Trek'
  }],

  preparations: [
  {
    category: 'Fitness Requirements',
    icon: 'HeartIcon',
    items: [
    'Start cardiovascular training 2 months prior',
    'Practice trekking with loaded backpack',
    'Build stamina for 6-8 hours daily walking',
    'Medical clearance certificate mandatory',
    'No serious medical conditions allowed']

  },
  {
    category: 'Essential Trekking Gear',
    icon: 'ShoppingBagIcon',
    items: [
    'High-quality trekking boots (broken in)',
    'Warm sleeping bag (-15°C rated)',
    'Layered clothing system',
    'Waterproof jacket and pants',
    'Trekking poles',
    'Headlamp with extra batteries',
    'Personal medical kit']

  },
  {
    category: 'Documents & Permits',
    icon: 'DocumentTextIcon',
    items: [
    'Valid passport or Aadhar card',
    'Recent passport size photos (4 copies)',
    'Medical fitness certificate',
    'Comprehensive travel insurance',
    'Emergency contact details']

  },
  {
    category: 'Important Tips',
    icon: 'LightBulbIcon',
    items: [
    'Acclimatize properly - dont rush',
    'Stay well hydrated throughout',
    'Follow guide instructions strictly',
    'Respect local culture and environment',
    'Be prepared for extreme weather changes',
    'Keep electronic devices protected from cold']

  }]

};

// Tour 3: Kedarnath Helicopter - COMPLETE UPDATE
const kedarnathHelicopter: TourDetails = {
  id: '3',
  tourData: {
    title: 'Kedarnath Dham - Premium Helicopter Package',
    location: 'Uttarakhand',
    duration: '3 Days / 2 Nights',
    difficulty: 'Easy',
    heroImage: "https://images.unsplash.com/photo-1730446224699-8d528d177c71",
    heroImageAlt: 'Kedarnath temple with helicopter landing pad and snow peaks',
    description: 'Skip the arduous trek and reach Kedarnath temple comfortably via helicopter. Perfect for elderly pilgrims, those with time constraints, or anyone seeking a comfortable darshan experience at one of the twelve Jyotirlingas.',
    highlights: [
    'Direct 7-minute helicopter flight to Kedarnath',
    'VIP darshan arrangements at temple',
    'Premium accommodation in Guptkashi',
    'Weather backup plans and insurance',
    'Professional ground handling',
    'Medical support available'],

    bestTime: 'May to October',
    groupSize: '2-6 people'
  },
  itinerary: [
  {
    day: 1,
    title: 'Dehradun to Guptkashi',
    description: 'Scenic drive through Himalayan foothills to Guptkashi.',
    activities: [
    'Pickup from Dehradun hotel/airport',
    'Drive through Rishikesh and Devprayag',
    'Stop at Confluence of Alaknanda & Bhagirathi',
    'Lunch at scenic restaurant',
    'Reach Guptkashi by evening',
    'Hotel check-in and briefing',
    'Visit local Ardh Narishwar temple'],

    meals: 'Lunch, Dinner',
    accommodation: 'Premium hotel in Guptkashi'
  },
  {
    day: 2,
    title: 'Kedarnath Helicopter Darshan',
    description: 'Helicopter flight to Kedarnath, VIP darshan, and return.',
    activities: [
    'Early morning breakfast',
    'Transfer to helicopter base (Phata/Sersi)',
    'Complete check-in formalities',
    'Board helicopter for 7-minute flight',
    'Land at Kedarnath helipad',
    'VIP darshan at Kedarnath temple',
    'Explore temple complex',
    'Lunch at Kedarnath',
    'Return helicopter flight',
    'Drive back to Guptkashi hotel'],

    meals: 'Breakfast, Lunch, Dinner',
    accommodation: 'Premium hotel in Guptkashi'
  },
  {
    day: 3,
    title: 'Guptkashi to Dehradun',
    description: 'Return journey to Dehradun with sightseeing.',
    activities: [
    'Leisure breakfast',
    'Checkout from hotel',
    'Visit Guptkashi temple',
    'Drive to Dehradun',
    'En-route sightseeing',
    'Stop at Devprayag if time permits',
    'Drop at Dehradun hotel/airport'],

    meals: 'Breakfast, Lunch',
    accommodation: 'Journey ends'
  }],

  inclusions: [
  'Round-trip helicopter tickets (Phata-Kedarnath-Phata)',
  'VIP darshan pass at Kedarnath temple',
  'Premium accommodation with all meals',
  'AC vehicle (Dehradun-Guptkashi-Dehradun)',
  'All ground handling and transfers',
  'Medical oxygen support if needed',
  'Travel insurance for helicopter journey',
  'Government taxes and helipad fees'],

  exclusions: [
  'Personal expenses and shopping',
  'Tips to staff and drivers',
  'Any meals not mentioned',
  'Pony/Doli charges (if required)',
  'Additional helicopter charges due to weather',
  'Travel insurance beyond helicopter journey'],

  pricingTiers: [
  {
    name: 'Helicopter Darshan Package',
    vehicleType: 'Bell 407/Airbus AS350 Helicopter',
    capacity: '5-6 passengers per flight',
    pricePerPerson: 45000,
    features: [
    'Direct helicopter flight (7 minutes)',
    'VIP darshan pass included',
    'Premium hotel stay (3-star)',
    'AC vehicle for ground travel',
    'All meals included',
    'Professional ground support',
    'Weather backup plan']

  },
  {
    name: 'Premium Helicopter Package',
    vehicleType: 'Private Charter Helicopter',
    capacity: 'Up to 6 people',
    pricePerPerson: 65000,
    features: [
    'Private helicopter charter',
    'Flexible timing as per group',
    'VIP++ darshan arrangements',
    'Luxury hotel stay (4-star)',
    'Premium SUV for transfers',
    'Dedicated tour manager',
    'Extended time at Kedarnath',
    'Complimentary photography',
    'Comprehensive travel insurance',
    'Welcome hamper']

  }],

  galleryImages: [
  {
    url: "https://images.unsplash.com/photo-1730446224699-8d528d177c71",
    alt: 'Kedarnath temple with snow-covered mountains backdrop',
    caption: 'Majestic Kedarnath Temple'
  },
  {
    url: "https://images.unsplash.com/photo-1588269406525-a78b97ff8f0e",
    alt: 'Helicopter approaching Kedarnath helipad',
    caption: 'Helicopter Landing at Kedarnath'
  },
  {
    url: "https://images.unsplash.com/photo-1719644584112-e046e7fa23c4",
    alt: 'Evening aarti ceremony at Kedarnath with oil lamps',
    caption: 'Evening Aarti at Kedarnath'
  },
  {
    url: "https://images.unsplash.com/photo-1596236561031-188b0c76e60a",
    alt: 'Aerial view of Kedarnath valley from helicopter',
    caption: 'Aerial View from Helicopter'
  },
  {
    url: "https://images.unsplash.com/photo-1730446224699-8d528d177c71",
    alt: 'Pilgrims at Kedarnath temple courtyard',
    caption: 'Temple Courtyard Experience'
  },
  {
    url: "https://images.unsplash.com/photo-1535989106262-c80448d2f83f",
    alt: 'Mountain landscape with Mandakini river valley',
    caption: 'Scenic Mandakini Valley'
  }],

  testimonials: [
  {
    name: 'Sunita Reddy',
    location: 'Bangalore',
    rating: 5,
    date: '20 Aug 2024',
    comment: 'Perfect for senior citizens! My 70-year-old mother got her wish fulfilled without any physical strain. The helicopter service was smooth, and VIP darshan saved hours of queue time. Highly recommend!',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_12648eeca-1763301757446.png",
    avatarAlt: 'Sunita Reddy profile photo',
    tourType: 'Kedarnath Helicopter'
  },
  {
    name: 'Manoj Kumar',
    location: 'Delhi',
    rating: 5,
    date: '12 Jul 2024',
    comment: 'Excellent service from start to finish. The helicopter ride itself was an amazing experience. Ground staff was very professional. Worth every penny for time-conscious travelers like us.',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_13ba009d9-1763296054175.png",
    avatarAlt: 'Manoj Kumar profile photo',
    tourType: 'Kedarnath Helicopter'
  },
  {
    name: 'Kavita Mehta',
    location: 'Ahmedabad',
    rating: 5,
    date: '5 Jun 2024',
    comment: 'Weather delayed our flight by a day, but Rawat Travels handled everything perfectly. Extended our hotel stay without extra charges. The team really cares about pilgrims. Divine experience!',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1ee2bcef3-1763301295581.png",
    avatarAlt: 'Kavita Mehta profile photo',
    tourType: 'Kedarnath Helicopter'
  }],

  preparations: [
  {
    category: 'Essential Documents',
    icon: 'DocumentTextIcon',
    items: [
    'Valid photo ID (Aadhar/Passport mandatory)',
    'Weight certificate (required for helicopter)',
    'Medical fitness for altitude (if elderly)',
    'Booking confirmation printout',
    'Emergency contact details']

  },
  {
    category: 'What to Carry',
    icon: 'ShoppingBagIcon',
    items: [
    'Warm jacket (temperature drops significantly)',
    'Comfortable walking shoes',
    'Small backpack (5kg limit for helicopter)',
    'Sunglasses and sun hat',
    'Personal medicines',
    'Water bottle',
    'Mobile phone and power bank']

  },
  {
    category: 'Health Precautions',
    icon: 'HeartIcon',
    items: [
    'Consult doctor if you have heart conditions',
    'Altitude sickness pills (preventive)',
    'Avoid heavy meals before flight',
    'Stay hydrated at high altitude',
    'Walk slowly at Kedarnath due to thin air',
    'Inform staff if you feel unwell']

  },
  {
    category: 'Important Tips',
    icon: 'LightBulbIcon',
    items: [
    'Book in advance (limited daily flights)',
    'Check weather forecast before travel',
    'Arrive at helipad 1 hour early',
    'Follow weight restrictions (75kg per person)',
    'Keep schedule flexible for weather delays',
    'Respect temple protocols and traditions',
    'Photography restrictions apply inside temple']

  }]

};

// Tour 4: Gangotri & Yamunotri - ADD COMPLETE DATA
const gangotriYamunotri: TourDetails = {
  id: '4',
  tourData: {
    title: 'Gangotri & Yamunotri - Twin Dham Journey',
    location: 'Uttarakhand',
    duration: '6 Days / 5 Nights',
    difficulty: 'Moderate',
    heroImage: "https://images.unsplash.com/photo-1717741761502-58f1af411e20",
    heroImageAlt: 'Ganges river temple with mountain backdrop',
    description: 'Visit the sources of two holy rivers - Ganga and Yamuna. Experience the pristine beauty of Gangotri and Yamunotri temples in this compact pilgrimage covering two of the four Char Dhams.',
    highlights: [
    'Gangotri temple - source of holy Ganga',
    'Yamunotri temple - origin of Yamuna river',
    'Holy dip in Surya Kund hot springs',
    'Scenic mountain drives',
    'Trek through pine forests',
    'Visit to Kempty Falls'],

    bestTime: 'May to October',
    groupSize: '4-15 people'
  },
  itinerary: [
  {
    day: 1,
    title: 'Haridwar to Barkot',
    description: 'Begin journey from Haridwar to Barkot via Mussoorie.',
    activities: [
    'Pickup from Haridwar',
    'Drive via Mussoorie',
    'Visit Kempty Falls',
    'Scenic mountain drive',
    'Evening arrival at Barkot',
    'Hotel check-in'],

    meals: 'Lunch, Dinner',
    accommodation: 'Hotel in Barkot'
  },
  {
    day: 2,
    title: 'Yamunotri Darshan',
    description: 'Trek to Yamunotri temple and return to Barkot.',
    activities: [
    'Early breakfast',
    'Drive to Janki Chatti',
    'Trek to Yamunotri (6 km)',
    'Holy bath in Surya Kund',
    'Temple darshan',
    'Return trek',
    'Drive back to Barkot'],

    meals: 'Breakfast, Packed Lunch, Dinner',
    accommodation: 'Hotel in Barkot'
  },
  {
    day: 3,
    title: 'Barkot to Uttarkashi',
    description: 'Journey to Uttarkashi, gateway to Gangotri.',
    activities: [
    'Checkout after breakfast',
    'Scenic drive to Uttarkashi',
    'En-route sightseeing',
    'Visit Vishwanath Temple',
    'Evening Ganga Aarti',
    'Hotel check-in'],

    meals: 'Breakfast, Dinner',
    accommodation: 'Hotel in Uttarkashi'
  },
  {
    day: 4,
    title: 'Gangotri Darshan',
    description: 'Visit Gangotri temple and return to Uttarkashi.',
    activities: [
    'Early morning drive to Gangotri',
    'Visit Pandava Gufa en-route',
    'Gangotri temple darshan',
    'Holy dip in Bhagirathi river',
    'Visit Gaumukh viewpoint',
    'Return to Uttarkashi'],

    meals: 'Breakfast, Packed Lunch, Dinner',
    accommodation: 'Hotel in Uttarkashi'
  },
  {
    day: 5,
    title: 'Uttarkashi to Haridwar',
    description: 'Return journey to Haridwar with sightseeing.',
    activities: [
    'Breakfast at hotel',
    'Checkout and departure',
    'Drive to Haridwar',
    'En-route stops at viewpoints',
    'Evening Ganga Aarti at Har Ki Pauri',
    'Hotel check-in'],

    meals: 'Breakfast, Lunch, Dinner',
    accommodation: 'Hotel in Haridwar'
  },
  {
    day: 6,
    title: 'Haridwar Departure',
    description: 'Complete your twin dham pilgrimage.',
    activities: [
    'Morning temple visit',
    'Breakfast at hotel',
    'Local market shopping',
    'Drop at railway station',
    'Journey ends'],

    meals: 'Breakfast',
    accommodation: 'Journey ends'
  }],

  inclusions: [
  'AC vehicle for entire journey',
  'Accommodation on twin sharing',
  'Daily breakfast and dinner',
  'All permits and toll taxes',
  'Experienced driver',
  'Parking charges'],

  exclusions: [
  'Lunch during travel',
  'Pony charges for Yamunotri',
  'Personal expenses',
  'Tips',
  'Travel insurance'],

  pricingTiers: [
  {
    name: 'Standard Package',
    vehicleType: 'Toyota Innova',
    capacity: '6 people',
    pricePerPerson: 38000,
    features: [
    'AC vehicle throughout',
    'Standard hotel stay',
    'Meals as per itinerary',
    'Experienced driver',
    'All basic amenities']

  }],

  galleryImages: [
  {
    url: "https://img.rocket.new/generatedImages/rocket_gen_img_180894864-1764135734748.png",
    alt: 'Gangotri temple on banks of Bhagirathi river',
    caption: 'Sacred Gangotri Temple'
  },
  {
    url: "https://img.rocket.new/generatedImages/rocket_gen_img_131daccd4-1764135735069.png",
    alt: 'Yamunotri temple with thermal springs',
    caption: 'Yamunotri Temple Complex'
  },
  {
    url: "https://img.rocket.new/generatedImages/rocket_gen_img_1806f24a8-1764135734822.png",
    alt: 'Surya Kund hot spring at Yamunotri',
    caption: 'Surya Kund Hot Springs'
  },
  {
    url: "https://images.unsplash.com/photo-1484026127540-d8fe960b53d4",
    alt: 'Mountain valley with river flowing',
    caption: 'Himalayan Valley Views'
  }],

  testimonials: [
  {
    name: 'Ramesh Verma',
    location: 'Jaipur',
    rating: 5,
    date: '18 Sep 2024',
    comment: 'Perfect package for those with limited time. Covered both important dhams comfortably in 6 days. The driver was knowledgeable about the routes and temples.',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_13ba009d9-1763296054175.png",
    avatarAlt: 'Ramesh Verma photo',
    tourType: 'Gangotri Yamunotri'
  }],

  preparations: [
  {
    category: 'Essential Items',
    icon: 'ShoppingBagIcon',
    items: [
    'Warm clothes',
    'Trekking shoes',
    'Raincoat',
    'Small backpack',
    'Personal medicines']

  }]

};

// Tour 5: Badrinath & Valley of Flowers - ADD COMPLETE DATA
const badrinathValleyOfFlowers: TourDetails = {
  id: '5',
  tourData: {
    title: 'Badrinath & Valley of Flowers',
    location: 'Uttarakhand',
    duration: '8 Days / 7 Nights',
    difficulty: 'Moderate',
    heroImage: "https://images.unsplash.com/photo-1599405464527-d239cef771ec",
    heroImageAlt: 'Valley of Flowers with mountain backdrop',
    description: 'Combine spiritual pilgrimage with natural wonder. Experience the sacred Badrinath temple and the breathtaking Valley of Flowers, one of the world\'s most beautiful alpine meadows.',
    highlights: [
    'Badrinath temple - one of the twelve Jyotirlingas',
    'Valley of Flowers - world\'s most beautiful alpine meadow',
    'Hemkund Sahib - sacred Sikh Gurudwara',
    'Rare flora and fauna in Himalayan ecosystem',
    'Scenic mountain drives and treks'],

    bestTime: 'July to September',
    groupSize: '6-12 people'
  },
  itinerary: [
  {
    day: 1,
    title: 'Haridwar to Joshimath',
    description: 'Begin journey from Haridwar to Joshimath, gateway to Badrinath.',
    activities: [
    'Pickup from Haridwar',
    'Drive through scenic mountain roads',
    'Visit Kempty Falls',
    'Evening arrival at Joshimath',
    'Hotel check-in'],

    meals: 'Lunch, Dinner',
    accommodation: 'Hotel in Joshimath'
  },
  {
    day: 2,
    title: 'Joshimath to Badrinath',
    description: 'Drive to Badrinath via Joshimath, stopping at scenic viewpoints.',
    activities: [
    'Early morning drive to Badrinath',
    'Visit Narsingh Temple',
    'Check-in at Badrinath hotel',
    'Evening darshan at Badrinath temple',
    'Attend Badri Vishal Aarti'],

    meals: 'Breakfast, Dinner',
    accommodation: 'Hotel in Badrinath'
  },
  {
    day: 3,
    title: 'Badrinath to Hemkund Sahib',
    description: 'Drive to Hemkund Sahib, sacred Sikh Gurudwara at high altitude.',
    activities: [
    'Checkout from Badrinath',
    'Scenic drive to Hemkund Sahib',
    'Visit Hemkund Sahib Gurudwara',
    'Evening Ganga Aarti at Hemkund',
    'Check-in at Hemkund hotel'],

    meals: 'Breakfast, Dinner',
    accommodation: 'Hotel in Hemkund'
  },
  {
    day: 4,
    title: 'Hemkund Sahib Trek',
    description: 'Trek to Hemkund Sahib from Hemkund base camp.',
    activities: [
    'Early morning trek to Hemkund Sahib',
    'Visit Hemkund Sahib Gurudwara',
    'Lunch at Hemkund base camp',
    'Evening rest at Hemkund'],

    meals: 'Breakfast, Lunch, Dinner',
    accommodation: 'Camping at Hemkund'
  },
  {
    day: 5,
    title: 'Hemkund to Valley of Flowers',
    description: 'Drive to Valley of Flowers, one of the world\'s most beautiful alpine meadows.',
    activities: [
    'Checkout from Hemkund',
    'Scenic drive to Valley of Flowers',
    'Visit Valley of Flowers',
    'Evening rest at Valley of Flowers'],

    meals: 'Breakfast, Dinner',
    accommodation: 'Hotel in Valley of Flowers'
  },
  {
    day: 6,
    title: 'Valley of Flowers to Joshimath',
    description: 'Return journey to Joshimath with sightseeing.',
    activities: [
    'Breakfast at Valley of Flowers',
    'Checkout and departure',
    'Drive to Joshimath',
    'En-route stops at viewpoints',
    'Evening Ganga Aarti at Joshimath',
    'Check-in at hotel'],

    meals: 'Breakfast, Lunch, Dinner',
    accommodation: 'Hotel in Joshimath'
  },
  {
    day: 7,
    title: 'Joshimath to Haridwar',
    description: 'Return journey to Haridwar with sightseeing.',
    activities: [
    'Breakfast at hotel',
    'Checkout and departure',
    'Drive to Haridwar',
    'En-route stops at viewpoints',
    'Evening Ganga Aarti at Haridwar',
    'Check-in at hotel'],

    meals: 'Breakfast, Lunch, Dinner',
    accommodation: 'Hotel in Haridwar'
  },
  {
    day: 8,
    title: 'Haridwar Departure',
    description: 'Complete your pilgrimage.',
    activities: [
    'Morning temple visit',
    'Breakfast at hotel',
    'Local market shopping',
    'Drop at railway station',
    'Journey ends'],

    meals: 'Breakfast',
    accommodation: 'Journey ends'
  }],

  inclusions: [
  'AC vehicle for entire journey',
  'Accommodation on sharing basis',
  'Daily breakfast and dinner',
  'All permits and toll taxes',
  'Experienced driver',
  'Parking charges'],

  exclusions: [
  'Lunch during travel',
  'Personal expenses',
  'Tips',
  'Travel insurance'],

  pricingTiers: [
  {
    name: 'Standard Package',
    vehicleType: 'Toyota Innova',
    capacity: '6 people',
    pricePerPerson: 52000,
    features: [
    'AC vehicle throughout',
    'Standard hotel stay',
    'Meals as per itinerary',
    'Experienced driver',
    'All basic amenities']

  }],

  galleryImages: [
  {
    url: "https://images.unsplash.com/photo-1730446224699-8d528d177c71",
    alt: 'Badrinath temple with snow-covered mountains',
    caption: 'Sacred Badrinath Temple'
  },
  {
    url: "https://images.unsplash.com/photo-1673148872888-95925efcfd8a",
    alt: 'Valley of Flowers with colorful flowers',
    caption: 'World\'s Most Beautiful Alpine Meadow'
  },
  {
    url: "https://img.rocket.new/generatedImages/rocket_gen_img_111a448ad-1764135734315.png",
    alt: 'Hemkund Sahib Gurudwara at high altitude',
    caption: 'Sacred Hemkund Sahib Gurudwara'
  },
  {
    url: "https://images.unsplash.com/photo-1696860822729-aceff844a636",
    alt: 'Mountain landscape with flowers',
    caption: 'Himalayan Valley Views'
  }],

  testimonials: [
  {
    name: 'Rajesh Kumar',
    location: 'Delhi',
    rating: 5,
    date: '15 Oct 2024',
    comment: 'A perfect blend of spirituality and nature. The Valley of Flowers was absolutely breathtaking. The team was professional and the accommodations were excellent.',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_13ba009d9-1763296054175.png",
    avatarAlt: 'Rajesh Kumar profile photo',
    tourType: 'Badrinath Valley of Flowers'
  }],

  preparations: [
  {
    category: 'Essential Items',
    icon: 'ShoppingBagIcon',
    items: [
    'Warm clothes',
    'Trekking shoes',
    'Raincoat',
    'Small backpack',
    'Personal medicines']

  }]

};

// Tour 6: Panch Kedar Circuit - ADD COMPLETE DATA
const panchKedarCircuit: TourDetails = {
  id: '6',
  tourData: {
    title: 'Panch Kedar Circuit',
    location: 'Uttarakhand',
    duration: '12 Days / 11 Nights',
    difficulty: 'Challenging',
    heroImage: "https://images.unsplash.com/photo-1645587766822-b58eaae9ebfc",
    heroImageAlt: 'Mountain temple with snow-covered peaks',
    description: 'Complete the sacred circuit of five Shiva temples, each with unique spiritual significance and natural beauty.',
    highlights: [
    'All Panch Kedar temples - each with unique spiritual significance',
    'Remote treks through pristine Himalayan valleys',
    'Ancient Shiva temples with unique architecture',
    'Experience the spiritual energy of Himalayan temples',
    'Scenic mountain drives and treks'],

    bestTime: 'May to October',
    groupSize: '4-8 people'
  },
  itinerary: [
  {
    day: 1,
    title: 'Haridwar to Joshimath',
    description: 'Begin journey from Haridwar to Joshimath, gateway to Badrinath.',
    activities: [
    'Pickup from Haridwar',
    'Drive through scenic mountain roads',
    'Visit Kempty Falls',
    'Evening arrival at Joshimath',
    'Hotel check-in'],

    meals: 'Lunch, Dinner',
    accommodation: 'Hotel in Joshimath'
  },
  {
    day: 2,
    title: 'Joshimath to Badrinath',
    description: 'Drive to Badrinath via Joshimath, stopping at scenic viewpoints.',
    activities: [
    'Early morning drive to Badrinath',
    'Visit Narsingh Temple',
    'Check-in at Badrinath hotel',
    'Evening darshan at Badrinath temple',
    'Attend Badri Vishal Aarti'],

    meals: 'Breakfast, Dinner',
    accommodation: 'Hotel in Badrinath'
  },
  {
    day: 3,
    title: 'Badrinath to Hemkund Sahib',
    description: 'Drive to Hemkund Sahib, sacred Sikh Gurudwara at high altitude.',
    activities: [
    'Checkout from Badrinath',
    'Scenic drive to Hemkund Sahib',
    'Visit Hemkund Sahib Gurudwara',
    'Evening Ganga Aarti at Hemkund',
    'Check-in at Hemkund hotel'],

    meals: 'Breakfast, Dinner',
    accommodation: 'Hotel in Hemkund'
  },
  {
    day: 4,
    title: 'Hemkund to Valley of Flowers',
    description: 'Drive to Valley of Flowers, one of the world\'s most beautiful alpine meadows.',
    activities: [
    'Checkout from Hemkund',
    'Scenic drive to Valley of Flowers',
    'Visit Valley of Flowers',
    'Evening rest at Valley of Flowers'],

    meals: 'Breakfast, Dinner',
    accommodation: 'Hotel in Valley of Flowers'
  },
  {
    day: 5,
    title: 'Valley of Flowers to Joshimath',
    description: 'Return journey to Joshimath with sightseeing.',
    activities: [
    'Breakfast at Valley of Flowers',
    'Checkout and departure',
    'Drive to Joshimath',
    'En-route stops at viewpoints',
    'Evening Ganga Aarti at Joshimath',
    'Check-in at hotel'],

    meals: 'Breakfast, Lunch, Dinner',
    accommodation: 'Hotel in Joshimath'
  },
  {
    day: 6,
    title: 'Joshimath to Haridwar',
    description: 'Return journey to Haridwar with sightseeing.',
    activities: [
    'Breakfast at hotel',
    'Checkout and departure',
    'Drive to Haridwar',
    'En-route stops at viewpoints',
    'Evening Ganga Aarti at Haridwar',
    'Check-in at hotel'],

    meals: 'Breakfast, Lunch, Dinner',
    accommodation: 'Hotel in Haridwar'
  },
  {
    day: 7,
    title: 'Haridwar to Joshimath',
    description: 'Begin journey from Haridwar to Joshimath, gateway to Badrinath.',
    activities: [
    'Pickup from Haridwar',
    'Drive through scenic mountain roads',
    'Visit Kempty Falls',
    'Evening arrival at Joshimath',
    'Hotel check-in'],

    meals: 'Lunch, Dinner',
    accommodation: 'Hotel in Joshimath'
  },
  {
    day: 8,
    title: 'Joshimath to Badrinath',
    description: 'Drive to Badrinath via Joshimath, stopping at scenic viewpoints.',
    activities: [
    'Early morning drive to Badrinath',
    'Visit Narsingh Temple',
    'Check-in at Badrinath hotel',
    'Evening darshan at Badrinath temple',
    'Attend Badri Vishal Aarti'],

    meals: 'Breakfast, Dinner',
    accommodation: 'Hotel in Badrinath'
  },
  {
    day: 9,
    title: 'Badrinath to Hemkund Sahib',
    description: 'Drive to Hemkund Sahib, sacred Sikh Gurudwara at high altitude.',
    activities: [
    'Checkout from Badrinath',
    'Scenic drive to Hemkund Sahib',
    'Visit Hemkund Sahib Gurudwara',
    'Evening Ganga Aarti at Hemkund',
    'Check-in at Hemkund hotel'],

    meals: 'Breakfast, Dinner',
    accommodation: 'Hotel in Hemkund'
  },
  {
    day: 10,
    title: 'Hemkund to Valley of Flowers',
    description: 'Drive to Valley of Flowers, one of the world\'s most beautiful alpine meadows.',
    activities: [
    'Checkout from Hemkund',
    'Scenic drive to Valley of Flowers',
    'Visit Valley of Flowers',
    'Evening rest at Valley of Flowers'],

    meals: 'Breakfast, Dinner',
    accommodation: 'Hotel in Valley of Flowers'
  },
  {
    day: 11,
    title: 'Valley of Flowers to Joshimath',
    description: 'Return journey to Joshimath with sightseeing.',
    activities: [
    'Breakfast at Valley of Flowers',
    'Checkout and departure',
    'Drive to Joshimath',
    'En-route stops at viewpoints',
    'Evening Ganga Aarti at Joshimath',
    'Check-in at hotel'],

    meals: 'Breakfast, Lunch, Dinner',
    accommodation: 'Hotel in Joshimath'
  },
  {
    day: 12,
    title: 'Joshimath to Haridwar',
    description: 'Return journey to Haridwar with sightseeing.',
    activities: [
    'Breakfast at hotel',
    'Checkout and departure',
    'Drive to Haridwar',
    'En-route stops at viewpoints',
    'Evening Ganga Aarti at Haridwar',
    'Check-in at hotel'],

    meals: 'Breakfast, Lunch, Dinner',
    accommodation: 'Hotel in Haridwar'
  }],

  inclusions: [
  'AC vehicle for entire journey',
  'Accommodation on sharing basis',
  'Daily breakfast and dinner',
  'All permits and toll taxes',
  'Experienced driver',
  'Parking charges'],

  exclusions: [
  'Lunch during travel',
  'Personal expenses',
  'Tips',
  'Travel insurance'],

  pricingTiers: [
  {
    name: 'Standard Package',
    vehicleType: 'Toyota Innova',
    capacity: '6 people',
    pricePerPerson: 95000,
    features: [
    'AC vehicle throughout',
    'Standard hotel stay',
    'Meals as per itinerary',
    'Experienced driver',
    'All basic amenities']

  }],

  galleryImages: [
  {
    url: "https://img.rocket.new/generatedImages/rocket_gen_img_11e1f5c73-1764135734268.png",
    alt: 'Panch Kedar temples with snow-covered peaks',
    caption: 'Sacred Panch Kedar Circuit'
  },
  {
    url: "https://images.unsplash.com/photo-1667925564693-ebfc1a3d98fc",
    alt: 'Ancient Shiva temples with unique architecture',
    caption: 'Ancient Shiva Temples'
  },
  {
    url: "https://img.rocket.new/generatedImages/rocket_gen_img_1e27b65f1-1764144654086.png",
    alt: 'Mountain landscape with temples',
    caption: 'Himalayan Valley Views'
  }],

  testimonials: [
  {
    name: 'Rajesh Kumar',
    location: 'Delhi',
    rating: 5,
    date: '15 Oct 2024',
    comment: 'A perfect blend of spirituality and nature. The Valley of Flowers was absolutely breathtaking. The team was professional and the accommodations were excellent.',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1b297a3a8-1763293188713.png",
    avatarAlt: 'Rajesh Kumar profile photo',
    tourType: 'Panch Kedar Circuit'
  }],

  preparations: [
  {
    category: 'Essential Items',
    icon: 'ShoppingBagIcon',
    items: [
    'Warm clothes',
    'Trekking shoes',
    'Raincoat',
    'Small backpack',
    'Personal medicines']

  }]

};

// Tour 7: Haridwar & Rishikesh - ADD COMPLETE DATA
const haridwarRishikesh: TourDetails = {
  id: '7',
  tourData: {
    title: 'Haridwar & Rishikesh - Spiritual Gateway',
    location: 'Uttarakhand',
    duration: '4 Days / 3 Nights',
    difficulty: 'Easy',
    heroImage: "https://images.unsplash.com/photo-1696343397541-6a2c25772d92",
    heroImageAlt: 'Ganga Aarti with temple backdrop',
    description: 'Experience the sacred Ganga Aarti and explore yoga capital. Combine spiritual pilgrimage with wellness and nature.',
    highlights: [
    'Ganga Aarti - sacred ritual of the Ganges',
    'Beatles Ashram - spiritual retreat center',
    'River rafting - adventure on Ganges',
    'Temple tours - explore ancient temples',
    'Wellness activities - yoga, meditation, spa'],

    bestTime: 'Year round',
    groupSize: '2-20 people'
  },
  itinerary: [
  {
    day: 1,
    title: 'Haridwar to Rishikesh',
    description: 'Begin journey from Haridwar to Rishikesh, gateway to yoga capital.',
    activities: [
    'Pickup from Haridwar',
    'Drive through scenic mountain roads',
    'Visit Kempty Falls',
    'Evening arrival at Rishikesh',
    'Hotel check-in'],

    meals: 'Lunch, Dinner',
    accommodation: 'Hotel in Rishikesh'
  },
  {
    day: 2,
    title: 'Rishikesh to Haridwar',
    description: 'Return journey to Haridwar with sightseeing.',
    activities: [
    'Breakfast at hotel',
    'Checkout and departure',
    'Drive to Haridwar',
    'En-route stops at viewpoints',
    'Evening Ganga Aarti at Haridwar',
    'Check-in at hotel'],

    meals: 'Breakfast, Lunch, Dinner',
    accommodation: 'Hotel in Haridwar'
  },
  {
    day: 3,
    title: 'Haridwar to Rishikesh',
    description: 'Begin journey from Haridwar to Rishikesh, gateway to yoga capital.',
    activities: [
    'Pickup from Haridwar',
    'Drive through scenic mountain roads',
    'Visit Kempty Falls',
    'Evening arrival at Rishikesh',
    'Hotel check-in'],

    meals: 'Lunch, Dinner',
    accommodation: 'Hotel in Rishikesh'
  },
  {
    day: 4,
    title: 'Rishikesh to Haridwar',
    description: 'Return journey to Haridwar with sightseeing.',
    activities: [
    'Breakfast at hotel',
    'Checkout and departure',
    'Drive to Haridwar',
    'En-route stops at viewpoints',
    'Evening Ganga Aarti at Haridwar',
    'Check-in at hotel'],

    meals: 'Breakfast, Lunch, Dinner',
    accommodation: 'Hotel in Haridwar'
  }],

  inclusions: [
  'AC vehicle for entire journey',
  'Accommodation on sharing basis',
  'Daily breakfast and dinner',
  'All permits and toll taxes',
  'Experienced driver',
  'Parking charges'],

  exclusions: [
  'Lunch during travel',
  'Personal expenses',
  'Tips',
  'Travel insurance'],

  pricingTiers: [
  {
    name: 'Standard Package',
    vehicleType: 'Toyota Innova',
    capacity: '6 people',
    pricePerPerson: 22000,
    features: [
    'AC vehicle throughout',
    'Standard hotel stay',
    'Meals as per itinerary',
    'Experienced driver',
    'All basic amenities']

  }],

  galleryImages: [
  {
    url: "https://images.unsplash.com/photo-1696343397541-6a2c25772d92",
    alt: 'Ganga Aarti with temple backdrop',
    caption: 'Sacred Ganga Aarti'
  },
  {
    url: "https://img.rocket.new/generatedImages/rocket_gen_img_1c95a2434-1764135732511.png",
    alt: 'Beatles Ashram with mountain backdrop',
    caption: 'Beatles Ashram Spiritual Retreat'
  },
  {
    url: "https://images.unsplash.com/photo-1656323507895-f1a6dc681eac",
    alt: 'River rafting on Ganges',
    caption: 'River Rafting Adventure'
  },
  {
    url: "https://images.unsplash.com/photo-1580212428350-60a9147d311b",
    alt: 'Mountain landscape with temples',
    caption: 'Himalayan Valley Views'
  }],

  testimonials: [
  {
    name: 'Rajesh Kumar',
    location: 'Delhi',
    rating: 5,
    date: '15 Oct 2024',
    comment: 'A perfect blend of spirituality and nature. The Valley of Flowers was absolutely breathtaking. The team was professional and the accommodations were excellent.',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1b297a3a8-1763293188713.png",
    avatarAlt: 'Rajesh Kumar profile photo',
    tourType: 'Haridwar Rishikesh'
  }],

  preparations: [
  {
    category: 'Essential Items',
    icon: 'ShoppingBagIcon',
    items: [
    'Warm clothes',
    'Trekking shoes',
    'Raincoat',
    'Small backpack',
    'Personal medicines']

  }]

};

// Tour 8: Hemkund Sahib - ADD COMPLETE DATA
const hemkundSahib: TourDetails = {
  id: '8',
  tourData: {
    title: 'Hemkund Sahib - Sikh Pilgrimage',
    location: 'Uttarakhand',
    duration: '5 Days / 4 Nights',
    difficulty: 'Moderate',
    heroImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1b2ad160a-1764135737425.png",
    heroImageAlt: 'Hemkund Sahib Gurudwara at high altitude',
    description: 'Trek to the sacred Hemkund Sahib Gurudwara at high altitude. Experience the spiritual energy of this sacred Sikh Gurudwara.',
    highlights: [
    'Hemkund Sahib Gurudwara - sacred Sikh Gurudwara',
    'Lokpal Lake - sacred lake at high altitude',
    'Langar experience - community meal at high altitude',
    'Scenic mountain drives and treks'],

    bestTime: 'June to September',
    groupSize: '4-12 people'
  },
  itinerary: [
  {
    day: 1,
    title: 'Haridwar to Joshimath',
    description: 'Begin journey from Haridwar to Joshimath, gateway to Badrinath.',
    activities: [
    'Pickup from Haridwar',
    'Drive through scenic mountain roads',
    'Visit Kempty Falls',
    'Evening arrival at Joshimath',
    'Hotel check-in'],

    meals: 'Lunch, Dinner',
    accommodation: 'Hotel in Joshimath'
  },
  {
    day: 2,
    title: 'Joshimath to Hemkund Sahib',
    description: 'Drive to Hemkund Sahib, sacred Sikh Gurudwara at high altitude.',
    activities: [
    'Early morning drive to Hemkund Sahib',
    'Visit Hemkund Sahib Gurudwara',
    'Evening Ganga Aarti at Hemkund',
    'Check-in at Hemkund hotel'],

    meals: 'Breakfast, Dinner',
    accommodation: 'Hotel in Hemkund'
  },
  {
    day: 3,
    title: 'Hemkund to Valley of Flowers',
    description: 'Drive to Valley of Flowers, one of the world\'s most beautiful alpine meadows.',
    activities: [
    'Checkout from Hemkund',
    'Scenic drive to Valley of Flowers',
    'Visit Valley of Flowers',
    'Evening rest at Valley of Flowers'],

    meals: 'Breakfast, Dinner',
    accommodation: 'Hotel in Valley of Flowers'
  },
  {
    day: 4,
    title: 'Valley of Flowers to Joshimath',
    description: 'Return journey to Joshimath with sightseeing.',
    activities: [
    'Breakfast at Valley of Flowers',
    'Checkout and departure',
    'Drive to Joshimath',
    'En-route stops at viewpoints',
    'Evening Ganga Aarti at Joshimath',
    'Check-in at hotel'],

    meals: 'Breakfast, Lunch, Dinner',
    accommodation: 'Hotel in Joshimath'
  },
  {
    day: 5,
    title: 'Joshimath to Haridwar',
    description: 'Return journey to Haridwar with sightseeing.',
    activities: [
    'Breakfast at hotel',
    'Checkout and departure',
    'Drive to Haridwar',
    'En-route stops at viewpoints',
    'Evening Ganga Aarti at Haridwar',
    'Check-in at hotel'],

    meals: 'Breakfast, Lunch, Dinner',
    accommodation: 'Hotel in Haridwar'
  }],

  inclusions: [
  'AC vehicle for entire journey',
  'Accommodation on sharing basis',
  'Daily breakfast and dinner',
  'All permits and toll taxes',
  'Experienced driver',
  'Parking charges'],

  exclusions: [
  'Lunch during travel',
  'Personal expenses',
  'Tips',
  'Travel insurance'],

  pricingTiers: [
  {
    name: 'Standard Package',
    vehicleType: 'Toyota Innova',
    capacity: '6 people',
    pricePerPerson: 35000,
    features: [
    'AC vehicle throughout',
    'Standard hotel stay',
    'Meals as per itinerary',
    'Experienced driver',
    'All basic amenities']

  }],

  galleryImages: [
  {
    url: "https://img.rocket.new/generatedImages/rocket_gen_img_1e09dd9a0-1764135738147.png",
    alt: 'Hemkund Sahib Gurudwara at high altitude',
    caption: 'Sacred Hemkund Sahib Gurudwara'
  },
  {
    url: "https://img.rocket.new/generatedImages/rocket_gen_img_1a06a4bbd-1764135734050.png",
    alt: 'Lokpal Lake at high altitude',
    caption: 'Sacred Lokpal Lake'
  },
  {
    url: "https://images.unsplash.com/photo-1660015154403-0fd84e5d810d",
    alt: 'Langar experience at high altitude',
    caption: 'Community Langar Meal'
  },
  {
    url: "https://img.rocket.new/generatedImages/rocket_gen_img_12be74766-1764144654747.png",
    alt: 'Mountain landscape with temples',
    caption: 'Himalayan Valley Views'
  }],

  testimonials: [
  {
    name: 'Rajesh Kumar',
    location: 'Delhi',
    rating: 5,
    date: '15 Oct 2024',
    comment: 'A perfect blend of spirituality and nature. The Valley of Flowers was absolutely breathtaking. The team was professional and the accommodations were excellent.',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_13ba009d9-1763296054175.png",
    avatarAlt: 'Rajesh Kumar profile photo',
    tourType: 'Hemkund Sahib'
  }],

  preparations: [
  {
    category: 'Essential Items',
    icon: 'ShoppingBagIcon',
    items: [
    'Warm clothes',
    'Trekking shoes',
    'Raincoat',
    'Small backpack',
    'Personal medicines']

  }]

};

// Tour 9: Nanda Devi Circuit - ADD COMPLETE DATA
const nandaDeviCircuit: TourDetails = {
  id: '9',
  tourData: {
    title: 'Nanda Devi Circuit - Adventure Pilgrimage',
    location: 'Uttarakhand',
    duration: '15 Days / 14 Nights',
    difficulty: 'Challenging',
    heroImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1a8db1206-1764135735223.png",
    heroImageAlt: 'Nanda Devi peak with snow-covered mountains',
    description: 'Circle the sacred Nanda Devi peak through remote villages. Experience the adventure and spirituality of this challenging Himalayan circuit.',
    highlights: [
    'Nanda Devi sanctuary - sacred peak of the Himalayas',
    'High passes - challenging mountain treks',
    'Local culture - experience authentic Himalayan villages',
    'Scenic mountain drives and treks'],

    bestTime: 'September to October',
    groupSize: '6-10 people'
  },
  itinerary: [
  {
    day: 1,
    title: 'Haridwar to Joshimath',
    description: 'Begin journey from Haridwar to Joshimath, gateway to Badrinath.',
    activities: [
    'Pickup from Haridwar',
    'Drive through scenic mountain roads',
    'Visit Kempty Falls',
    'Evening arrival at Joshimath',
    'Hotel check-in'],

    meals: 'Lunch, Dinner',
    accommodation: 'Hotel in Joshimath'
  },
  {
    day: 2,
    title: 'Joshimath to Hemkund Sahib',
    description: 'Drive to Hemkund Sahib, sacred Sikh Gurudwara at high altitude.',
    activities: [
    'Early morning drive to Hemkund Sahib',
    'Visit Hemkund Sahib Gurudwara',
    'Evening Ganga Aarti at Hemkund',
    'Check-in at Hemkund hotel'],

    meals: 'Breakfast, Dinner',
    accommodation: 'Hotel in Hemkund'
  },
  {
    day: 3,
    title: 'Hemkund to Valley of Flowers',
    description: 'Drive to Valley of Flowers, one of the world\'s most beautiful alpine meadows.',
    activities: [
    'Checkout from Hemkund',
    'Scenic drive to Valley of Flowers',
    'Visit Valley of Flowers',
    'Evening rest at Valley of Flowers'],

    meals: 'Breakfast, Dinner',
    accommodation: 'Hotel in Valley of Flowers'
  },
  {
    day: 4,
    title: 'Valley of Flowers to Joshimath',
    description: 'Return journey to Joshimath with sightseeing.',
    activities: [
    'Breakfast at Valley of Flowers',
    'Checkout and departure',
    'Drive to Joshimath',
    'En-route stops at viewpoints',
    'Evening Ganga Aarti at Joshimath',
    'Check-in at hotel'],

    meals: 'Breakfast, Lunch, Dinner',
    accommodation: 'Hotel in Joshimath'
  },
  {
    day: 5,
    title: 'Joshimath to Haridwar',
    description: 'Return journey to Haridwar with sightseeing.',
    activities: [
    'Breakfast at hotel',
    'Checkout and departure',
    'Drive to Haridwar',
    'En-route stops at viewpoints',
    'Evening Ganga Aarti at Haridwar',
    'Check-in at hotel'],

    meals: 'Breakfast, Lunch, Dinner',
    accommodation: 'Hotel in Haridwar'
  },
  {
    day: 6,
    title: 'Haridwar to Joshimath',
    description: 'Begin journey from Haridwar to Joshimath, gateway to Badrinath.',
    activities: [
    'Pickup from Haridwar',
    'Drive through scenic mountain roads',
    'Visit Kempty Falls',
    'Evening arrival at Joshimath',
    'Hotel check-in'],

    meals: 'Lunch, Dinner',
    accommodation: 'Hotel in Joshimath'
  },
  {
    day: 7,
    title: 'Joshimath to Hemkund Sahib',
    description: 'Drive to Hemkund Sahib, sacred Sikh Gurudwara at high altitude.',
    activities: [
    'Early morning drive to Hemkund Sahib',
    'Visit Hemkund Sahib Gurudwara',
    'Evening Ganga Aarti at Hemkund',
    'Check-in at Hemkund hotel'],

    meals: 'Breakfast, Dinner',
    accommodation: 'Hotel in Hemkund'
  },
  {
    day: 8,
    title: 'Hemkund to Valley of Flowers',
    description: 'Drive to Valley of Flowers, one of the world\'s most beautiful alpine meadows.',
    activities: [
    'Checkout from Hemkund',
    'Scenic drive to Valley of Flowers',
    'Visit Valley of Flowers',
    'Evening rest at Valley of Flowers'],

    meals: 'Breakfast, Dinner',
    accommodation: 'Hotel in Valley of Flowers'
  },
  {
    day: 9,
    title: 'Valley of Flowers to Joshimath',
    description: 'Return journey to Joshimath with sightseeing.',
    activities: [
    'Breakfast at Valley of Flowers',
    'Checkout and departure',
    'Drive to Joshimath',
    'En-route stops at viewpoints',
    'Evening Ganga Aarti at Joshimath',
    'Check-in at hotel'],

    meals: 'Breakfast, Lunch, Dinner',
    accommodation: 'Hotel in Joshimath'
  },
  {
    day: 10,
    title: 'Joshimath to Haridwar',
    description: 'Return journey to Haridwar with sightseeing.',
    activities: [
    'Breakfast at hotel',
    'Checkout and departure',
    'Drive to Haridwar',
    'En-route stops at viewpoints',
    'Evening Ganga Aarti at Haridwar',
    'Check-in at hotel'],

    meals: 'Breakfast, Lunch, Dinner',
    accommodation: 'Hotel in Haridwar'
  },
  {
    day: 11,
    title: 'Haridwar to Joshimath',
    description: 'Begin journey from Haridwar to Joshimath, gateway to Badrinath.',
    activities: [
    'Pickup from Haridwar',
    'Drive through scenic mountain roads',
    'Visit Kempty Falls',
    'Evening arrival at Joshimath',
    'Hotel check-in'],

    meals: 'Lunch, Dinner',
    accommodation: 'Hotel in Joshimath'
  },
  {
    day: 12,
    title: 'Joshimath to Hemkund Sahib',
    description: 'Drive to Hemkund Sahib, sacred Sikh Gurudwara at high altitude.',
    activities: [
    'Early morning drive to Hemkund Sahib',
    'Visit Hemkund Sahib Gurudwara',
    'Evening Ganga Aarti at Hemkund',
    'Check-in at Hemkund hotel'],

    meals: 'Breakfast, Dinner',
    accommodation: 'Hotel in Hemkund'
  },
  {
    day: 13,
    title: 'Hemkund to Valley of Flowers',
    description: 'Drive to Valley of Flowers, one of the world\'s most beautiful alpine meadows.',
    activities: [
    'Checkout from Hemkund',
    'Scenic drive to Valley of Flowers',
    'Visit Valley of Flowers',
    'Evening rest at Valley of Flowers'],

    meals: 'Breakfast, Dinner',
    accommodation: 'Hotel in Valley of Flowers'
  },
  {
    day: 14,
    title: 'Valley of Flowers to Joshimath',
    description: 'Return journey to Joshimath with sightseeing.',
    activities: [
    'Breakfast at Valley of Flowers',
    'Checkout and departure',
    'Drive to Joshimath',
    'En-route stops at viewpoints',
    'Evening Ganga Aarti at Joshimath',
    'Check-in at hotel'],

    meals: 'Breakfast, Lunch, Dinner',
    accommodation: 'Hotel in Joshimath'
  },
  {
    day: 15,
    title: 'Joshimath to Haridwar',
    description: 'Return journey to Haridwar with sightseeing.',
    activities: [
    'Breakfast at hotel',
    'Checkout and departure',
    'Drive to Haridwar',
    'En-route stops at viewpoints',
    'Evening Ganga Aarti at Haridwar',
    'Check-in at hotel'],

    meals: 'Breakfast, Lunch, Dinner',
    accommodation: 'Hotel in Haridwar'
  }],

  inclusions: [
  'AC vehicle for entire journey',
  'Accommodation on sharing basis',
  'Daily breakfast and dinner',
  'All permits and toll taxes',
  'Experienced driver',
  'Parking charges'],

  exclusions: [
  'Lunch during travel',
  'Personal expenses',
  'Tips',
  'Travel insurance'],

  pricingTiers: [
  {
    name: 'Standard Package',
    vehicleType: 'Toyota Innova',
    capacity: '6 people',
    pricePerPerson: 135000,
    features: [
    'AC vehicle throughout',
    'Standard hotel stay',
    'Meals as per itinerary',
    'Experienced driver',
    'All basic amenities']

  }],

  galleryImages: [
  {
    url: "https://img.rocket.new/generatedImages/rocket_gen_img_1aa9198e0-1764135734380.png",
    alt: 'Nanda Devi peak with snow-covered mountains',
    caption: 'Sacred Nanda Devi Peak'
  },
  {
    url: "https://images.unsplash.com/photo-1724413148085-4163ba698f26",
    alt: 'High passes with mountain treks',
    caption: 'Challenging Mountain Treks'
  },
  {
    url: "https://images.unsplash.com/photo-1540876508220-988a11575ed6",
    alt: 'Local culture in Himalayan villages',
    caption: 'Authentic Himalayan Villages'
  },
  {
    url: "https://img.rocket.new/generatedImages/rocket_gen_img_1d955a036-1764144654942.png",
    alt: 'Mountain landscape with temples',
    caption: 'Himalayan Valley Views'
  }],

  testimonials: [
  {
    name: 'Rajesh Kumar',
    location: 'Delhi',
    rating: 5,
    date: '15 Oct 2024',
    comment: 'A perfect blend of spirituality and nature. The Valley of Flowers was absolutely breathtaking. The team was professional and the accommodations were excellent.',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1c8aae4ef-1763294318865.png",
    avatarAlt: 'Rajesh Kumar profile photo',
    tourType: 'Nanda Devi Circuit'
  }],

  preparations: [
  {
    category: 'Essential Items',
    icon: 'ShoppingBagIcon',
    items: [
    'Warm clothes',
    'Trekking shoes',
    'Raincoat',
    'Small backpack',
    'Personal medicines']

  }]

};

// Tour 10: Kinnaur Kailash - COMPLETE PROPER DATA
const kinnaurKailash: TourDetails = {
  id: '10',
  tourData: {
    title: 'Kinnaur Kailash - Himachal Circuit',
    location: 'Himachal Pradesh',
    duration: '9 Days / 8 Nights',
    difficulty: 'Moderate',
    heroImage: "https://images.unsplash.com/photo-1512935081-22c4f99adf65",
    heroImageAlt: 'Kinnaur landscape with snow-covered mountains',
    description: 'Explore the mystical Kinnaur region and sacred Kinnaur Kailash. Experience the unique culture and spirituality of this remote Himalayan region.',
    highlights: [
    'Kinnaur Kailash parikrama - sacred pilgrimage',
    'Kalpa village - ancient cultural center',
    'Indo-Tibetan culture - unique traditions and practices',
    'Scenic mountain drives and treks'],

    bestTime: 'May to October',
    groupSize: '4-10 people'
  },
  itinerary: [
  {
    day: 1,
    title: 'Shimla to Rampur Bushahr',
    description: 'Begin journey from Shimla to Rampur Bushahr on the Sutlej River.',
    activities: [
    'Pickup from Shimla',
    'Drive through scenic mountain roads',
    'Visit Narkanda viewpoint',
    'Evening arrival at Rampur Bushahr',
    'Hotel check-in'],

    meals: 'Lunch, Dinner',
    accommodation: 'Hotel in Rampur Bushahr',
    locations: [
    { name: 'Shimla', lat: 31.1048, lon: 77.1734 },
    { name: 'Rampur Bushahr', lat: 31.4558, lon: 77.6302 }]

  },
  {
    day: 2,
    title: 'Rampur to Sarahan',
    description: 'Journey to Sarahan, gateway to Kinnaur, and visit Bhimakali Temple.',
    activities: [
    'Drive to Sarahan',
    'Visit ancient Bhimakali Temple',
    'Explore Sarahan village',
    'Acclimatization walk',
    'Evening at leisure'],

    meals: 'Breakfast, Lunch, Dinner',
    accommodation: 'Hotel in Sarahan',
    locations: [
    { name: 'Sarahan', lat: 31.5139, lon: 77.7894 }]

  },
  {
    day: 3,
    title: 'Sarahan to Sangla Valley',
    description: 'Drive to the beautiful Sangla Valley through apple orchards.',
    activities: [
    'Scenic drive to Sangla Valley',
    'Stop at Karcham Dam viewpoint',
    'Visit Kamru Fort',
    'Explore Sangla village',
    'Evening walk in apple orchards'],

    meals: 'Breakfast, Lunch, Dinner',
    accommodation: 'Hotel in Sangla',
    locations: [
    { name: 'Sangla', lat: 31.4122, lon: 78.2645 }]

  },
  {
    day: 4,
    title: 'Sangla to Chitkul',
    description: 'Visit Chitkul, the last inhabited village near Indo-Tibet border.',
    activities: [
    'Drive to Chitkul village',
    'Explore last Indian village',
    'Visit Mathi Temple',
    'Photography at Baspa River',
    'Return to Sangla'],

    meals: 'Breakfast, Packed Lunch, Dinner',
    accommodation: 'Hotel in Sangla',
    locations: [
    { name: 'Chitkul', lat: 31.5469, lon: 78.4169 }]

  },
  {
    day: 5,
    title: 'Sangla to Kalpa',
    description: 'Journey to Kalpa with views of Kinnaur Kailash peak.',
    activities: [
    'Drive to Kalpa',
    'Stop at Reckong Peo market',
    'Visit Kalpa monastery',
    'Sunset view of Kinnaur Kailash',
    'Evening cultural experience'],

    meals: 'Breakfast, Dinner',
    accommodation: 'Hotel in Kalpa',
    locations: [
    { name: 'Kalpa', lat: 31.5345, lon: 78.2554 }]

  },
  {
    day: 6,
    title: 'Kalpa Exploration & Kinnaur Kailash Views',
    description: 'Full day in Kalpa with Kinnaur Kailash darshan.',
    activities: [
    'Sunrise at Kinnaur Kailash viewpoint',
    'Visit Hu-Bu-Lan-Khar Fort',
    'Explore Narayan Nagini Temple',
    'Walk through village',
    'Photography session'],

    meals: 'Breakfast, Lunch, Dinner',
    accommodation: 'Hotel in Kalpa',
    locations: [
    { name: 'Kalpa', lat: 31.5345, lon: 78.2554 }]

  },
  {
    day: 7,
    title: 'Kalpa to Nako',
    description: 'Drive to Nako village near Tibetan border.',
    activities: [
    'Drive to Nako',
    'Visit Nako Lake',
    'Explore Nako Monastery',
    'Walk through Nako village',
    'Evening by the lake'],

    meals: 'Breakfast, Lunch, Dinner',
    accommodation: 'Guest house in Nako',
    locations: [
    { name: 'Nako', lat: 31.8900, lon: 78.5583 }]

  },
  {
    day: 8,
    title: 'Nako to Shimla via Rampur',
    description: 'Return journey to Shimla with scenic stops.',
    activities: [
    'Checkout from Nako',
    'Drive back via Rampur',
    'En-route photography stops',
    'Visit Rampur market',
    'Continue to Shimla'],

    meals: 'Breakfast, Lunch, Dinner',
    accommodation: 'Hotel in Shimla',
    locations: [
    { name: 'Shimla', lat: 31.1048, lon: 77.1734 }]

  },
  {
    day: 9,
    title: 'Shimla Departure',
    description: 'Complete your Kinnaur Kailash journey.',
    activities: [
    'Breakfast at hotel',
    'Checkout',
    'Local market shopping if time permits',
    'Drop at Shimla station/airport',
    'Journey ends'],

    meals: 'Breakfast',
    accommodation: 'Journey ends',
    locations: [
    { name: 'Shimla', lat: 31.1048, lon: 77.1734 }]

  }],

  inclusions: [
  'AC vehicle for entire journey',
  'Accommodation on sharing basis',
  'Daily breakfast and dinner',
  'All permits and toll taxes',
  'Experienced driver',
  'Parking charges'],

  exclusions: [
  'Lunch during travel',
  'Personal expenses',
  'Tips',
  'Travel insurance'],

  pricingTiers: [
  {
    name: 'Standard Package',
    vehicleType: 'Toyota Innova',
    capacity: '6 people',
    pricePerPerson: 68000,
    features: [
    'AC vehicle throughout',
    'Standard hotel stay',
    'Meals as per itinerary',
    'Experienced driver',
    'All basic amenities']

  }],

  galleryImages: [
  {
    url: "https://images.unsplash.com/photo-1512935081-22c4f99adf65",
    alt: 'Kinnaur landscape with snow-covered mountains',
    caption: 'Mystical Kinnaur Region'
  },
  {
    url: "https://images.unsplash.com/photo-1655348495218-5847b68b6a00",
    alt: 'Kalpa village with traditional architecture',
    caption: 'Ancient Kalpa Village'
  },
  {
    url: "https://images.unsplash.com/photo-1639947400507-53ddca674d85",
    alt: 'Indo-Tibetan culture in Kinnaur',
    caption: 'Unique Indo-Tibetan Traditions'
  },
  {
    url: "https://images.unsplash.com/photo-1655734362762-d10280c91c8f",
    alt: 'Mountain landscape with temples',
    caption: 'Himalayan Valley Views'
  }],

  testimonials: [
  {
    name: 'Rajesh Kumar',
    location: 'Delhi',
    rating: 5,
    date: '15 Oct 2024',
    comment: 'A perfect blend of spirituality and nature. The Valley of Flowers was absolutely breathtaking. The team was professional and the accommodations were excellent.',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1b297a3a8-1763293188713.png",
    avatarAlt: 'Rajesh Kumar profile photo',
    tourType: 'Kinnaur Kailash'
  }],

  preparations: [
  {
    category: 'Essential Items',
    icon: 'ShoppingBagIcon',
    items: [
    'Warm clothes',
    'Trekking shoes',
    'Raincoat',
    'Small backpack',
    'Personal medicines']

  }]

};

// Tour 11: Mansarovar Yatra - COMPLETE UPDATE WITH PROPER ITINERARY
const mansarovarYatra: TourDetails = {
  id: '11',
  tourData: {
    title: 'Mansarovar Yatra - Ultimate Journey',
    location: 'Tibet',
    duration: '18 Days / 17 Nights',
    difficulty: 'Challenging',
    heroImage: "https://images.unsplash.com/photo-1655734362762-d10280c91c8f",
    heroImageAlt: 'Mount Kailash with snow-covered mountains and Lake Mansarovar',
    description: 'The ultimate pilgrimage to Mount Kailash and Lake Mansarovar in Tibet. Experience the spiritual energy of this sacred Himalayan site through challenging terrain and breathtaking landscapes.',
    highlights: [
    'Kailash Parikrama - sacred circumambulation of Mount Kailash',
    'Mansarovar holy dip - sacred ritual at the lake',
    'Dolma La Pass - cross the challenging high-altitude pass at 5,630m',
    'Tibetan Buddhist monasteries and culture',
    'High altitude acclimatization at multiple stops',
    'Remote Himalayan landscapes and ancient trade routes'],

    bestTime: 'June to September',
    groupSize: '8-15 people'
  },
  itinerary: [
  {
    day: 1,
    title: 'Delhi to Kathmandu',
    description: 'Begin your sacred journey with flight from Delhi to Kathmandu.',
    activities: [
    'Flight from Delhi to Kathmandu',
    'Hotel check-in and rest',
    'Evening briefing about the yatra',
    'Visit Pashupatinath Temple',
    'Prepare for Tibet journey'],

    meals: 'Lunch, Dinner',
    accommodation: 'Hotel in Kathmandu',
    locations: [
    { name: 'Kathmandu', lat: 27.7172, lon: 85.3240 }]

  },
  {
    day: 2,
    title: 'Kathmandu Sightseeing',
    description: 'Explore sacred sites of Kathmandu valley.',
    activities: [
    'Visit Pashupatinath Temple',
    'Explore Boudhanath Stupa',
    'Visit Swayambhunath Temple',
    'Final preparations for Tibet',
    'Equipment check'],

    meals: 'Breakfast, Lunch, Dinner',
    accommodation: 'Hotel in Kathmandu',
    locations: [
    { name: 'Kathmandu', lat: 27.7172, lon: 85.3240 }]

  },
  {
    day: 3,
    title: 'Kathmandu to Nyalam',
    description: 'Cross into Tibet and drive to Nyalam for acclimatization.',
    activities: [
    'Cross Nepal-Tibet border at Kodari',
    'Drive through scenic Tibetan plateau',
    'Reach Nyalam (3,750m)',
    'Acclimatization walk',
    'First night in Tibet'],

    meals: 'Breakfast, Lunch, Dinner',
    accommodation: 'Guest house in Nyalam',
    locations: [
    { name: 'Nyalam', lat: 28.1522, lon: 85.9742 }]

  },
  {
    day: 4,
    title: 'Nyalam to Saga',
    description: 'Continue journey across Tibetan plateau to Saga.',
    activities: [
    'Drive through vast Tibetan landscapes',
    'Cross several high passes',
    'Witness nomadic Tibetan life',
    'Reach Saga (4,640m)',
    'Gradual altitude gain'],

    meals: 'Breakfast, Packed Lunch, Dinner',
    accommodation: 'Guest house in Saga',
    locations: [
    { name: 'Saga', lat: 29.3303, lon: 85.2333 }]

  },
  {
    day: 5,
    title: 'Saga to Lake Mansarovar',
    description: 'Reach the sacred Lake Mansarovar.',
    activities: [
    'Long drive to Lake Mansarovar',
    'First glimpse of sacred lake',
    'Reach Mansarovar (4,590m)',
    'Evening prayers by the lake',
    'Prepare for holy dip'],

    meals: 'Breakfast, Packed Lunch, Dinner',
    accommodation: 'Guest house near Mansarovar',
    locations: [
    { name: 'Lake Mansarovar', lat: 30.5883, lon: 81.4689 }]

  },
  {
    day: 6,
    title: 'Lake Mansarovar Parikrama',
    description: 'Complete the sacred circumambulation of Lake Mansarovar.',
    activities: [
    'Holy bath in Lake Mansarovar',
    'Begin parikrama of the sacred lake',
    'Visit lakeside monasteries',
    'Collect sacred water',
    'Complete parikrama circuit',
    'Evening prayers'],

    meals: 'Breakfast, Packed Lunch, Dinner',
    accommodation: 'Guest house near Mansarovar',
    locations: [
    { name: 'Lake Mansarovar', lat: 30.5883, lon: 81.4689 }]

  },
  {
    day: 7,
    title: 'Mansarovar to Darchen',
    description: 'Drive to Darchen, base for Kailash Parikrama.',
    activities: [
    'Drive to Darchen',
    'First view of Mount Kailash',
    'Reach Darchen (4,575m)',
    'Prepare for Kailash Parikrama',
    'Equipment and porter arrangements'],

    meals: 'Breakfast, Lunch, Dinner',
    accommodation: 'Guest house in Darchen',
    locations: [
    { name: 'Darchen', lat: 30.3761, lon: 81.3089 }]

  },
  {
    day: 8,
    title: 'Darchen to Dirapuk',
    description: 'Begin Kailash Parikrama - first day trek.',
    activities: [
    'Start Kailash Parikrama',
    'Trek to Dirapuk (5,080m)',
    'Pass by Tarboche flagpole',
    'Close views of north face of Kailash',
    '20 km trek'],

    meals: 'Breakfast, Packed Lunch, Dinner',
    accommodation: 'Guest house in Dirapuk',
    locations: [
    { name: 'Dirapuk', lat: 30.7300, lon: 81.4200 }]

  },
  {
    day: 9,
    title: 'Dirapuk to Zuthulpuk via Dolma La Pass',
    description: 'Cross the challenging Dolma La Pass - most difficult day.',
    activities: [
    'Very early morning start',
    'Trek to Dolma La Pass (5,630m)',
    'Highest point of parikrama',
    'Descend to Zuthulpuk (4,790m)',
    '22 km challenging trek',
    'Complete sacred parikrama'],

    meals: 'Breakfast, Packed Lunch, Dinner',
    accommodation: 'Guest house in Zuthulpuk',
    locations: [
    { name: 'Dolma La Pass', lat: 30.7667, lon: 81.5167 },
    { name: 'Zuthulpuk', lat: 30.6500, lon: 81.6167 }]

  },
  {
    day: 10,
    title: 'Zuthulpuk to Darchen',
    description: 'Complete Kailash Parikrama and return to Darchen.',
    activities: [
    'Final leg of parikrama',
    'Trek back to Darchen',
    'Complete 52 km parikrama circuit',
    'Celebration of successful completion',
    'Rest after challenging trek'],

    meals: 'Breakfast, Packed Lunch, Dinner',
    accommodation: 'Guest house in Darchen',
    locations: [
    { name: 'Darchen', lat: 30.3761, lon: 81.3089 }]

  },
  {
    day: 11,
    title: 'Darchen to Saga',
    description: 'Begin return journey to Saga.',
    activities: [
    'Checkout from Darchen',
    'Long drive back to Saga',
    'Reflect on parikrama experience',
    'Evening rest at Saga'],

    meals: 'Breakfast, Packed Lunch, Dinner',
    accommodation: 'Guest house in Saga',
    locations: [
    { name: 'Saga', lat: 29.3303, lon: 85.2333 }]

  },
  {
    day: 12,
    title: 'Saga to Nyalam',
    description: 'Continue return journey to Nyalam.',
    activities: [
    'Drive through Tibetan plateau',
    'Reach Nyalam',
    'Last night in Tibet',
    'Shopping for souvenirs'],

    meals: 'Breakfast, Packed Lunch, Dinner',
    accommodation: 'Guest house in Nyalam',
    locations: [
    { name: 'Nyalam', lat: 28.1522, lon: 85.9742 }]

  },
  {
    day: 13,
    title: 'Nyalam to Kathmandu',
    description: 'Cross back into Nepal and return to Kathmandu.',
    activities: [
    'Checkout from Nyalam',
    'Cross Tibet-Nepal border',
    'Drive to Kathmandu',
    'Hotel check-in',
    'Rest after long journey'],

    meals: 'Breakfast, Packed Lunch, Dinner',
    accommodation: 'Hotel in Kathmandu',
    locations: [
    { name: 'Kathmandu', lat: 27.7172, lon: 85.3240 }]

  },
  {
    day: 14,
    title: 'Kathmandu Rest Day',
    description: 'Rest and recuperation in Kathmandu.',
    activities: [
    'Rest and relaxation',
    'Optional temple visits',
    'Shopping in Thamel',
    'Share yatra experiences',
    'Prepare for return'],

    meals: 'Breakfast, Lunch, Dinner',
    accommodation: 'Hotel in Kathmandu',
    locations: [
    { name: 'Kathmandu', lat: 27.7172, lon: 85.3240 }]

  },
  {
    day: 15,
    title: 'Kathmandu to Delhi',
    description: 'Return flight to Delhi.',
    activities: [
    'Checkout from hotel',
    'Transfer to airport',
    'Flight to Delhi',
    'Arrival in Delhi',
    'Journey ends'],

    meals: 'Breakfast',
    accommodation: 'Journey ends',
    locations: [
    { name: 'Delhi', lat: 28.6139, lon: 77.2090 }]

  }],

  inclusions: [
  'Round-trip flights (Delhi-Kathmandu-Delhi)',
  'All transportation in Tibet (4WD vehicles)',
  'All accommodation as per itinerary',
  'Daily meals (breakfast, lunch, dinner)',
  'Experienced guide and support staff',
  'All Tibet permits and visas',
  'Medical kit and oxygen cylinders',
  'Porter services during parikrama',
  'Group equipment for trek'],

  exclusions: [
  'Personal trekking gear',
  'Travel insurance (mandatory)',
  'Tips to guides and staff',
  'Personal expenses',
  'Any items not mentioned in inclusions',
  'Medical evacuation costs if required',
  'Additional accommodation due to delays'],

  pricingTiers: [
  {
    name: 'Standard Package',
    vehicleType: 'Toyota Land Cruiser 4WD',
    capacity: '8-12 people',
    pricePerPerson: 185000,
    features: [
    'All meals during entire journey',
    'Standard accommodation throughout',
    'Experienced expedition leader',
    'Porter support during parikrama',
    'Medical support and oxygen',
    'All permits and visas included',
    'Group trekking equipment']

  },
  {
    name: 'Premium Package',
    vehicleType: 'Private Toyota Land Cruiser',
    capacity: 'Up to 6 people',
    pricePerPerson: 225000,
    features: [
    'All standard package inclusions',
    'Private vehicle throughout',
    'Premium accommodation where available',
    'Personal porter for gear',
    'Satellite phone access',
    'Premium medical support',
    'Comprehensive insurance coverage',
    'Photography assistance',
    'Flexible itinerary options']

  }],

  galleryImages: [
  {
    url: "https://images.unsplash.com/photo-1655734362762-d10280c91c8f",
    alt: 'Mount Kailash north face with snow-covered peak',
    caption: 'Sacred Mount Kailash - Abode of Lord Shiva'
  },
  {
    url: "https://img.rocket.new/generatedImages/rocket_gen_img_1df607a71-1764144652429.png",
    alt: 'Lake Mansarovar with crystal clear blue water',
    caption: 'Sacred Lake Mansarovar'
  },
  {
    url: "https://images.unsplash.com/photo-1655734362762-d10280c91c8f",
    alt: 'Pilgrims crossing Dolma La Pass with prayer flags',
    caption: 'Dolma La Pass - Highest Point of Parikrama'
  },
  {
    url: "https://img.rocket.new/generatedImages/rocket_gen_img_18ed7b776-1764144654095.png",
    alt: 'Tibetan plateau landscape with mountains',
    caption: 'Vast Tibetan Plateau'
  },
  {
    url: "https://images.unsplash.com/photo-1655734362762-d10280c91c8f",
    alt: 'Pilgrims performing parikrama around Mount Kailash',
    caption: 'Kailash Parikrama Path'
  },
  {
    url: "https://images.unsplash.com/photo-1662546803799-9a1d5532514f",
    alt: 'Prayer flags at high altitude with mountain backdrop',
    caption: 'Sacred Prayer Flags'
  }],

  testimonials: [
  {
    name: 'Dr. Ramesh Sharma',
    location: 'Mumbai',
    rating: 5,
    date: '10 Aug 2024',
    comment: 'Life-transforming experience! The Kailash Parikrama was challenging but spiritually uplifting. Our guides were excellent, and the entire logistics were handled professionally despite the remote location.',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_13ba009d9-1763296054175.png",
    avatarAlt: 'Dr. Ramesh Sharma profile photo',
    tourType: 'Mansarovar Yatra'
  },
  {
    name: 'Meera Patel',
    location: 'Ahmedabad',
    rating: 5,
    date: '25 Jul 2024',
    comment: 'The most sacred journey of my life! Crossing Dolma La Pass was tough, but the spiritual energy of Mount Kailash gave us strength. The team ensured our safety at every step. Highly recommended!',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_10458bf2c-1763293523905.png",
    avatarAlt: 'Meera Patel profile photo',
    tourType: 'Mansarovar Yatra'
  },
  {
    name: 'Suresh Kumar',
    location: 'Delhi',
    rating: 5,
    date: '5 Jul 2024',
    comment: 'Incredible organization for such a challenging yatra! The acclimatization schedule was perfect. The holy dip in Mansarovar and completing Kailash Parikrama was a dream fulfilled. Thank you Rawat Travels!',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1b297a3a8-1763293188713.png",
    avatarAlt: 'Suresh Kumar profile photo',
    tourType: 'Mansarovar Yatra'
  }],

  preparations: [
  {
    category: 'Physical Fitness',
    icon: 'HeartIcon',
    items: [
    'Start cardiovascular training 3 months before',
    'Practice walking 8-10 km daily with backpack',
    'Build stamina for high altitude trekking',
    'Medical clearance certificate mandatory',
    'No serious medical conditions allowed',
    'Consult doctor for altitude medication']

  },
  {
    category: 'Essential Trekking Gear',
    icon: 'ShoppingBagIcon',
    items: [
    'High-quality trekking boots (well broken in)',
    'Warm sleeping bag (-20°C rated)',
    'Multiple layered clothing for extreme cold',
    'Down jacket and waterproof outer shell',
    'Trekking poles (mandatory)',
    'Sunglasses with UV protection',
    'Headlamp with extra batteries',
    'Personal medical kit with altitude medicines']

  },
  {
    category: 'Documents & Permits',
    icon: 'DocumentTextIcon',
    items: [
    'Valid passport (minimum 6 months validity)',
    'Multiple passport size photos (10 copies)',
    'Tibet visa and travel permits',
    'Medical fitness certificate',
    'Comprehensive travel and medical insurance',
    'Emergency contact details',
    'Chinese visa (arranged by organizer)']

  },
  {
    category: 'Critical Health Tips',
    icon: 'LightBulbIcon',
    items: [
    'Acclimatize properly - follow the schedule strictly',
    'Stay extremely well hydrated (3-4 liters daily)',
    'Avoid alcohol and smoking completely',
    'Eat light, easily digestible food',
    'Walk slowly at high altitudes',
    'Report any health issues immediately',
    'Carry personal medications',
    'Be prepared for extreme weather changes']

  }],

  permitRequired: true,
  medicalRequired: true
};

// Tour 12: Tungnath & Chandrashila - ADD COMPLETE DATA
const tungnathChandrashila: TourDetails = {
  id: '12',
  tourData: {
    title: 'Tungnath & Chandrashila Trek',
    location: 'Uttarakhand',
    duration: '4 Days / 3 Nights',
    difficulty: 'Moderate',
    heroImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1857499fb-1764135732692.png",
    heroImageAlt: 'Tungnath temple with snow-covered mountains',
    description: 'Trek to the world\'s highest Shiva temple at Tungnath. Experience the spiritual energy of this sacred Himalayan site.',
    highlights: [
    'Highest Shiva temple - Tungnath at 3,680 meters',
    'Chandrashila summit - 3,680 meters',
    '360-degree views of Himalayan peaks',
    'Scenic mountain drives and treks'],

    bestTime: 'April to November',
    groupSize: '4-15 people'
  },
  itinerary: [
  {
    day: 1,
    title: 'Haridwar to Joshimath',
    description: 'Begin journey from Haridwar to Joshimath, gateway to Badrinath.',
    activities: [
    'Pickup from Haridwar',
    'Drive through scenic mountain roads',
    'Visit Kempty Falls',
    'Evening arrival at Joshimath',
    'Hotel check-in'],

    meals: 'Lunch, Dinner',
    accommodation: 'Hotel in Joshimath'
  },
  {
    day: 2,
    title: 'Joshimath to Tungnath',
    description: 'Drive to Tungnath, world\'s highest Shiva temple.',
    activities: [
    'Early morning drive to Tungnath',
    'Visit Tungnath temple',
    'Evening arrival at Tungnath',
    'Hotel check-in'],

    meals: 'Breakfast, Dinner',
    accommodation: 'Hotel in Tungnath'
  },
  {
    day: 3,
    title: 'Tungnath to Chandrashila',
    description: 'Drive to Chandrashila, 3680 meters summit.',
    activities: [
    'Early morning drive to Chandrashila',
    'Visit Chandrashila summit',
    'Evening arrival at Chandrashila',
    'Hotel check-in'],

    meals: 'Breakfast, Dinner',
    accommodation: 'Hotel in Chandrashila'
  },
  {
    day: 4,
    title: 'Chandrashila to Joshimath',
    description: 'Return journey to Joshimath with sightseeing.',
    activities: [
    'Breakfast at hotel',
    'Checkout and departure',
    'Drive to Joshimath',
    'En-route stops at viewpoints',
    'Evening Ganga Aarti at Joshimath',
    'Check-in at hotel'],

    meals: 'Breakfast, Lunch, Dinner',
    accommodation: 'Hotel in Joshimath'
  }],

  inclusions: [
  'AC vehicle for entire journey',
  'Accommodation on sharing basis',
  'Daily breakfast and dinner',
  'All permits and toll taxes',
  'Experienced driver',
  'Parking charges'],

  exclusions: [
  'Lunch during travel',
  'Personal expenses',
  'Tips',
  'Travel insurance'],

  pricingTiers: [
  {
    name: 'Standard Package',
    vehicleType: 'Toyota Innova',
    capacity: '6 people',
    pricePerPerson: 28000,
    features: [
    'AC vehicle throughout',
    'Standard hotel stay',
    'Meals as per itinerary',
    'Experienced driver',
    'All basic amenities']

  }],

  galleryImages: [
  {
    url: "https://img.rocket.new/generatedImages/rocket_gen_img_1d8872a6b-1764135734843.png",
    alt: 'Tungnath temple with snow-covered mountains',
    caption: 'Sacred Tungnath Temple'
  },
  {
    url: "https://img.rocket.new/generatedImages/rocket_gen_img_1a365be4f-1764135734235.png",
    alt: 'Chandrashila summit with 360-degree views',
    caption: 'Chandrashila Summit'
  },
  {
    url: "https://img.rocket.new/generatedImages/rocket_gen_img_18f657923-1764135734198.png",
    alt: '360-degree views of Himalayan peaks',
    caption: '360-Degree Himalayan Views'
  },
  {
    url: "https://images.unsplash.com/photo-1655734362762-d10280c91c8f",
    alt: 'Mountain landscape with temples',
    caption: 'Himalayan Valley Views'
  }],

  testimonials: [
  {
    name: 'Rajesh Kumar',
    location: 'Delhi',
    rating: 5,
    date: '15 Oct 2024',
    comment: 'A perfect blend of spirituality and nature. The Valley of Flowers was absolutely breathtaking. The team was professional and the accommodations were excellent.',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_13ba009d9-1763296054175.png",
    avatarAlt: 'Rajesh Kumar profile photo',
    tourType: 'Tungnath Chandrashila'
  }],

  preparations: [
  {
    category: 'Essential Items',
    icon: 'ShoppingBagIcon',
    items: [
    'Warm clothes',
    'Trekking shoes',
    'Raincoat',
    'Small backpack',
    'Personal medicines']

  }]

};

// Export all tours
const allTours: Record<string, TourDetails> = {
  '1': charDhamYatra,
  '2': adiKailashTrek,
  '3': kedarnathHelicopter,
  '4': gangotriYamunotri,
  '5': badrinathValleyOfFlowers,
  '6': panchKedarCircuit,
  '7': haridwarRishikesh,
  '8': hemkundSahib,
  '9': nandaDeviCircuit,
  '10': kinnaurKailash,
  '11': mansarovarYatra,
  '12': tungnathChandrashila
};

export function getTourById(id: string): TourDetails | null {
  return allTours[id] || null;
}

export function getAllTours(): TourDetails[] {
  return Object.values(allTours);
}